import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { updatePrimaryColor, updateSidebarColor } from '../../../../shared/globalStyling/ColorChanger';
import { useBreakpoints } from '../../../../shared/globalStyling/screenSizes';
import {
  AreaChartOutlined,
  BookOutlined,
  DollarCircleOutlined,
  SearchOutlined,
  DotChartOutlined,
  StarOutlined,
  ReadOutlined,
  FileTextOutlined,
  UsergroupAddOutlined,
  AppstoreAddOutlined,
  CompressOutlined,
  TagOutlined,
  EnvironmentOutlined,
  UnorderedListOutlined,
  FileAddOutlined,
  SettingOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  DashboardOutlined,
  BellOutlined,
  PlusOutlined,
  MoonOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

/**
 * TaskDesignPreview
 *
 * Live showcase of the Task Management design system.
 * Renders pure presentational markup using the same BEM CSS classes
 * as the real components — no GraphQL, no providers needed.
 *
 * Route: /admin/tasks/design-preview
 */

// Warm chrome palette — Zinc-based for refined editorial feel
// (Only affects presentation chrome; token data arrays stay unchanged)
const C = {
  text: 'var(--preview-text)',
  strong: 'var(--preview-strong)',
  body: 'var(--preview-body)',
  label: 'var(--preview-label)',
  secondary: 'var(--preview-secondary)',
  muted: 'var(--preview-muted)',
  faint: 'var(--preview-faint)',
  bg: 'var(--preview-bg)',
  bgAlt: 'var(--preview-bg-alt)',
  border: 'var(--preview-border)',
  borderMed: 'var(--preview-border-med)',
  mono: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
  sans: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const DEFAULT_PRIMARY = '#1890ff';
const DEFAULT_SIDEBAR_BG = '#f4f5f7';

// ---------------------------------------------------------------------------
// Design Token Constants — JS mirror of _task-tokens.scss
// Every inline visual property MUST reference T.* — no hardcoded values.
// Layout-only properties (display, flex, position, width, height) are exempt.
// ---------------------------------------------------------------------------
const T = {
  // Typography — 8-step ramp: Caption 12 / Small 13 / Button 14 / Body 15 / BodyL 16 / H3 18 / H2 22 / H1 28
  type: { caption: 12, small: 13, button: 14, body: 15, bodyLg: 16, h3: 18, h2: 22, h1: 28 },
  // Font weights — only 3 allowed ($font-weight-*)
  weight: { regular: 400, medium: 500, semibold: 600 },
  // Border radius ($radius-*)
  radius: { none: 0, xs: 4, sm: 6, md: 8, input: 10, lg: 12, xl: 16, xxl: 20, pill: 9999 },
  // Spacing — governed 8-value scale ($spacing-*)
  space: { xxs: 4, xs: 8, sm: 12, md: 16, lg: 24, xl: 32, xxl: 48, xxxl: 64 },
  // Icon glyph sizes — font-size for the icon itself ($icon-size-*)
  icon: { xs: 12, sm: 14, md: 16, lg: 18, xl: 20, xxl: 24 },
  // Icon box sizes — click-target container ($icon-box-*)
  iconBox: { xs: 24, sm: 28, md: 32, lg: 36, xl: 40 },
  // Component sizing contract — heights for interactive elements
  size: {
    btnSm: 32, btnMd: 36, btnDefault: 40, btnLg: 44,
    iconBtnSm: 28, iconBtnMd: 32,
    inputSm: 32, inputMd: 40, inputLg: 44,
    chipSm: 24, chipMd: 28, chipLg: 32,
    touchMin: 44,
  },
  // Semantic indicator colors (not theme-dependent)
  color: {
    allowed: '#16a34a', forbidden: '#dc2626', focus: '#2563eb', onDark: '#fff',
    urgencyHigh: '#f97316', infoDark: '#1e40af', warningDark: '#b45309',
    neutral: '#9e9e9e',
  },
};

/**
 * primaryRgba — reusable pattern for theme-aware transparent primary colors.
 * Uses CSS custom properties set by ColorChanger.js so colors update live.
 * @param {number} a - Alpha value (0–1)
 * @returns {string} CSS rgba() using custom properties with fallback
 */
const primaryRgba = (a) =>
  `rgba(var(--primary-red,24),var(--primary-green,144),var(--primary-blue,255),${a})`;

/**
 * sidebarRgba — reusable pattern for theme-aware transparent sidebar colors.
 * @param {number} a - Alpha value (0–1)
 * @returns {string} CSS rgba() using custom properties with fallback
 */
const sidebarRgba = (a) =>
  `rgba(var(--sidebar-red,0),var(--sidebar-green,21),var(--sidebar-blue,41),${a})`;

/** Read a live CSS custom property from :root */
const getCssVar = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const URGENCY_COLORS = {
  critical: { hex: '#dc2626', label: 'Critical' },
  high:     { hex: '#f97316', label: 'High' },
  medium:   { hex: '#eab308', label: 'Medium' },
  low:      { hex: '#22c55e', label: 'Low' },
};

const STATUS_LIST = [
  { key: 'todo',            label: 'Not Started',       color: '#94a3b8' },
  { key: 'triaged',         label: 'Triaged',           color: '#1890ff' },
  { key: 'in-review',       label: 'Ready for Review',  color: '#13c2c2' },
  { key: 'in-progress',     label: 'In Progress',       color: '#3b82f6' },
  { key: 'blocked',         label: 'Blocked',           color: '#dc2626' },
  { key: 'on-hold',         label: 'On Hold',           color: '#8b5cf6' },
  { key: 'done',            label: 'Completed',         color: '#22c55e' },
  { key: 'monitoring',      label: 'Monitoring',        color: '#722ed1' },
  { key: 'duplicate',       label: 'Duplicate',         color: '#8c8c8c' },
  { key: 'input-required',  label: 'Input Required',    color: '#fa8c16' },
  { key: 'not-needed',      label: 'No Longer Needed',  color: '#8c8c8c' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const Avatar = ({ initials, size = 28, color = 'var(--ant-primary-color, #1890ff)' }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      color: T.color.onDark,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.4,
      fontWeight: T.weight.semibold,
      border: '2px solid var(--preview-card-bg)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
      flexShrink: 0,
    }}
  >
    {initials}
  </div>
);

const StatusDot = ({ color, size = 8 }) => (
  <span
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
      boxShadow: `0 0 0 2px var(--preview-card-bg), 0 0 6px ${color}40`,
    }}
  />
);

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

/**
 * Section — reusable section wrapper with entrance animation and BEM structure.
 * @param {boolean} noPadMobile - If true, removes body padding on mobile for full-bleed content
 */
const Section = ({ title, description, children, id, noPadMobile }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Parse leading "N." from title to render as a styled number badge
  const match = typeof title === 'string' ? title.match(/^(\d+)\.\s*(.*)$/) : null;
  const sectionNumber = match ? match[1] : null;
  const titleText = match ? match[2] : title;

  const className = [
    'task-design-preview__section',
    visible && 'task-design-preview__section--visible',
  ].filter(Boolean).join(' ');

  return (
    <div className={className} ref={ref} id={id}>
      <div className="task-design-preview__section-header">
        <div className="task-design-preview__section-title">
          {sectionNumber && (
            <span className="task-design-preview__section-number">{sectionNumber}</span>
          )}
          {titleText}
        </div>
        {description && (
          <div className="task-design-preview__section-desc">{description}</div>
        )}
      </div>
      <div className={[
        'task-design-preview__section-body',
        noPadMobile && 'task-design-preview__section-body--no-pad-mobile',
      ].filter(Boolean).join(' ')}>
        {children}
      </div>
    </div>
  );
};

const SubSection = ({ label, children }) => (
  <div className="task-design-preview__subsection">
    {label && <div className="task-design-preview__subsection-label">{label}</div>}
    {children}
  </div>
);

// ===========================================================================
// SECTION COMPONENTS
//
// 4-group architecture (26 sections total):
//   A (1-6)   — Foundations: colors, typography, spacing, radius, elevation, motion
//   B (7-14)  — Components: buttons, forms, cards, badges, tables, modals, pagination, icons
//   C (15-22) — Patterns: header, filters, kanban, drawer, comments, sidebar, breakpoints, responsive
//   D (23-26) — Governance: accessibility, sizing contract, governance rules, date formats
//
// Definitions follow declaration order. The canonical layer sequence is
// established in TOC_ITEMS and the render return at the bottom of this file.
// ===========================================================================

// ---------------------------------------------------------------------------
// 15. Header & Navigation (Patterns)
// ---------------------------------------------------------------------------

const HeaderPreview = () => (
  <Section
    id="sec-15"
    title="15. Header & Navigation"
    description="Task board header with scope selector and view tabs, plus global app header and contextual footer."
  >
    <SubSection label="Task board header">
      <div className="task-board-header-modern">
        <div className="task-board-header__desktop">
          <div className="task-board-header__left">
            <div className="task-board-header__title-row">
              <h1 className="task-board-header__title">Task Management</h1>
              <button className="task-board-header__scope-switch" type="button" title="Switch scope">
                <span style={{ fontSize: T.type.small }}>&#8645;</span>
              </button>
            </div>
            <div className="task-board-header__context">
              Showing all tasks &middot; 24 total
            </div>
          </div>
          <div className="task-board-header__actions">
            <div className="task-board-header__view-tabs">
              <button className="task-view-tab task-view-tab--active" type="button">Board</button>
              <button className="task-view-tab" type="button">Table</button>
              <button className="task-view-tab" type="button">Gantt</button>
            </div>
          </div>
        </div>
      </div>
    </SubSection>

    {/* App Header & Footer (merged from Header/Footer section) */}
    <HeaderFooterContent />
  </Section>
);

// ---------------------------------------------------------------------------
// 16. Filter Bar (Patterns)
// ---------------------------------------------------------------------------

const FilterPreview = () => (
  <Section
    id="sec-16"
    title="16. Filter Bar"
    description="Collapsed and expanded states with smart filter chips."
  >
    <SubSection label="Smart filter chips">
      <div style={{ display: 'flex', gap: T.space.xs, flexWrap: 'wrap' }}>
        {['All Tasks', 'My Tasks', 'Overdue', 'High Priority', 'In Review'].map((chip) => (
          <span
            key={chip}
            className="task-smart-filters__chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${T.space.xxs}px ${T.space.md}px`,
              borderRadius: T.radius.pill,
              fontSize: T.type.caption,
              fontWeight: T.weight.medium,
              letterSpacing: '0.01em',
              border: chip === 'All Tasks' ? `1px solid ${primaryRgba(0.2)}` : '1px solid ' + C.border,
              background: chip === 'All Tasks' ? primaryRgba(0.08) : 'var(--preview-glass)',
              color: chip === 'All Tasks' ? 'var(--ant-primary-color, #1890ff)' : C.body,
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
              transition: 'all 180ms ease',
              boxShadow: chip === 'All Tasks' ? `0 1px 4px ${primaryRgba(0.1)}` : 'none',
            }}
          >
            {chip}
          </span>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 17. Kanban Board (Patterns)
// ---------------------------------------------------------------------------

const MOCK_CARDS = [
  { id: 1, title: 'Design new landing page', urgency: 'high', status: 'in-progress', assignee: 'AB' },
  { id: 2, title: 'Fix checkout flow regression', urgency: 'critical', status: 'blocked', assignee: 'CD' },
  { id: 3, title: 'Update API documentation', urgency: 'low', status: 'todo', assignee: 'EF' },
  { id: 4, title: 'Implement user onboarding', urgency: 'medium', status: 'triaged', assignee: 'GH' },
  { id: 5, title: 'Audit analytics events', urgency: 'low', status: 'done', assignee: 'IJ' },
  { id: 6, title: 'Review brand assets', urgency: 'medium', status: 'in-review', assignee: 'KL' },
  { id: 7, title: 'Migrate legacy endpoints', urgency: 'high', status: 'on-hold', assignee: 'MN' },
  { id: 8, title: 'Update privacy policy', urgency: 'low', status: 'monitoring', assignee: 'OP' },
  { id: 9, title: 'Duplicate ticket cleanup', urgency: 'low', status: 'duplicate', assignee: 'QR' },
  { id: 10, title: 'Await client approval', urgency: 'medium', status: 'input-required', assignee: 'ST' },
];

const KanbanCard = ({ card, modifier = '' }) => {
  const urgencyColor = URGENCY_COLORS[card.urgency]?.hex || T.color.neutral;
  const classes = [
    'tm-card',
    `tm-card--priority-${card.urgency}`,
    modifier,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div className="tm-card__urgency-stripe" style={{ background: urgencyColor }} />
      <div className="tm-card__content">
        <div className="tm-card__title">{card.title}</div>
        <div className="tm-card__meta">
          <span
            className={`task-table__inline-status task-table__inline-status--${card.status}`}
            style={{ fontSize: T.type.caption, padding: '1px 6px', borderRadius: T.radius.xs, fontWeight: T.weight.semibold, textTransform: 'uppercase' }}
          >
            {STATUS_LIST.find((s) => s.key === card.status)?.label || card.status}
          </span>
          <div className="tm-card__assignees">
            <Avatar initials={card.assignee} size={20} />
          </div>
        </div>
      </div>
    </div>
  );
};

const KanbanPreview = () => (
  <Section
    id="sec-17"
    title="17. Kanban Board"
    description="Columns with cards grouped by status. Includes normal and collapsed column."
  >
    <div className="task-design-preview__kanban-wrap">
      {/* Normal columns */}
      {['Not Started', 'In Progress', 'Blocked', 'Completed'].map((colName, colIdx) => {
        const statusKey = ['todo', 'in-progress', 'blocked', 'done'][colIdx];
        const colCards = MOCK_CARDS.filter((c) => c.status === statusKey);
        return (
          <div className="tm-column" key={colName} style={{ flex: '1 1 280px', minWidth: 280, maxWidth: 380 }}>
            <div className="tm-column__header">
              <span className="tm-column__title">{colName}</span>
              <span className="tm-column__count" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: T.size.chipSm, height: T.size.chipSm, borderRadius: '50%', background: 'var(--preview-subtle-bg)',
                fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.secondary, letterSpacing: '-0.02em',
              }}>
                {colCards.length}
              </span>
            </div>
            <div className="tm-column__cards">
              {colCards.map((card) => (
                <KanbanCard key={card.id} card={card} />
              ))}
              {colCards.length === 0 && (
                <div className="tm-column__empty" style={{ padding: T.space.xxl, textAlign: 'center', color: C.muted, fontSize: T.type.caption }}>
                  No tasks
                </div>
              )}
            </div>
          </div>
        );
      })}
      {/* Collapsed column */}
      <div
        className="tm-column tm-column--collapsed"
        style={{ flex: '0 0 60px', minWidth: 60, maxWidth: 60 }}
      >
        <div className="tm-column__header" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', padding: `${T.space.sm}px 0` }}>
          <span className="tm-column__title" style={{ fontSize: T.type.caption }}>On Hold</span>
          <span style={{ fontSize: T.type.caption, color: C.muted }}>2</span>
        </div>
      </div>
    </div>
  </Section>
);

// ---------------------------------------------------------------------------
// 9. Task Card Variants (Components)
// ---------------------------------------------------------------------------

const CardVariantsPreview = () => (
  <Section
    id="sec-9"
    title="9. Task Card Variants"
    description="Individual kanban cards showing different states and priority levels."
  >
    <div className="task-design-preview__row">
      <div className="task-design-preview__col">
        <SubSection label="Default (Low Priority)">
          <KanbanCard card={MOCK_CARDS[2]} />
        </SubSection>
        <SubSection label="High Priority">
          <KanbanCard card={MOCK_CARDS[0]} />
        </SubSection>
        <SubSection label="Critical Priority">
          <KanbanCard card={MOCK_CARDS[1]} />
        </SubSection>
      </div>
      <div className="task-design-preview__col">
        <SubSection label="Selected">
          <KanbanCard card={MOCK_CARDS[0]} modifier="tm-card--selected" />
        </SubSection>
        <SubSection label="Overdue">
          <KanbanCard card={MOCK_CARDS[1]} modifier="tm-card--overdue" />
        </SubSection>
        <SubSection label="Completed">
          <KanbanCard card={MOCK_CARDS[4]} modifier="tm-card--completed" />
        </SubSection>
      </div>
    </div>
  </Section>
);

// ---------------------------------------------------------------------------
// 11. Table View (Components)
// ---------------------------------------------------------------------------

const TABLE_ROWS = [
  { id: 'T-101', name: 'Create user auth flow',       urgency: 'critical', status: 'in-progress',    assignee: 'AB', due: '2026-02-20' },
  { id: 'T-102', name: 'Update dashboard charts',     urgency: 'high',     status: 'todo',           assignee: 'CD', due: '2026-03-01' },
  { id: 'T-103', name: 'Write integration tests',     urgency: 'medium',   status: 'in-review',      assignee: 'EF', due: '2026-02-15' },
  { id: 'T-104', name: 'Optimize query performance',  urgency: 'low',      status: 'done',           assignee: 'GH', due: '2026-02-10' },
  { id: 'T-105', name: 'Fix mobile navigation bug',   urgency: 'high',     status: 'blocked',        assignee: 'IJ', due: '2026-02-12' },
  { id: 'T-106', name: 'Triage new support tickets',  urgency: 'medium',   status: 'triaged',        assignee: 'KL', due: '2026-03-05' },
  { id: 'T-107', name: 'Monitor deploy stability',    urgency: 'low',      status: 'monitoring',     assignee: 'MN', due: '2026-02-28' },
  { id: 'T-108', name: 'Await client brand assets',   urgency: 'medium',   status: 'input-required', assignee: 'OP', due: '2026-03-10' },
  { id: 'T-109', name: 'Migrate to new auth provider', urgency: 'high',    status: 'on-hold',        assignee: 'QR', due: '2026-03-15' },
  { id: 'T-110', name: 'Remove deprecated API calls', urgency: 'low',      status: 'not-needed',     assignee: 'ST', due: '2026-02-18' },
];

const TaskTable = ({ compact = false }) => {
  const wrapperClass = compact ? 'tm-table tm-table--compact' : 'tm-table';
  return (
    <table className={wrapperClass}>
      <thead>
        <tr>
          {['ID', 'Task Name', 'Urgency', 'Status', 'Assignee', 'Due Date'].map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {TABLE_ROWS.map((row) => {
          const uColor = URGENCY_COLORS[row.urgency]?.hex || T.color.neutral;
          const statusInfo = STATUS_LIST.find((s) => s.key === row.status);
          return (
            <tr key={row.id} style={{ borderLeft: `3px solid ${uColor}` }}>
              <td className="tm-table__cell--mono">{row.id}</td>
              <td className="tm-table__cell--name">{row.name}</td>
              <td>
                <span className="tm-badge-urgency" style={{ '--badge-color': uColor }}>
                  {URGENCY_COLORS[row.urgency]?.label}
                </span>
              </td>
              <td>
                <span className="tm-badge-status" style={{ '--badge-color': statusInfo?.color || T.color.neutral }}>
                  <StatusDot color={statusInfo?.color} size={6} />
                  {statusInfo?.label}
                </span>
              </td>
              <td>
                <Avatar initials={row.assignee} size={compact ? 22 : 28} />
              </td>
              <td className="tm-table__cell--secondary">{row.due}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/**
 * TaskCardView — responsive card transformation of the task table.
 * Reusable pattern: On mobile, tables transform into expandable card lists.
 * Each card shows primary info (urgency stripe, ID, name, badges) with
 * a "More info" expansion for secondary columns (assignee, due date).
 */
const TaskCardView = () => {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="task-preview-card-list">
      {TABLE_ROWS.map((row) => {
        const uColor = URGENCY_COLORS[row.urgency]?.hex || T.color.neutral;
        const statusInfo = STATUS_LIST.find((s) => s.key === row.status);
        const isExpanded = expandedId === row.id;

        return (
          <div key={row.id} className="task-preview-card">
            <div className="task-preview-card__body">
              <div className="task-preview-card__stripe" style={{ background: uColor }} />
              <div className="task-preview-card__header">
                <div className="task-preview-card__id">{row.id}</div>
                <div className="task-preview-card__name">{row.name}</div>
                <div className="task-preview-card__badges">
                  <span
                    style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: T.radius.sm,
                      fontSize: T.type.caption, fontWeight: T.weight.semibold, textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background: `${uColor}10`, color: uColor, border: `1px solid ${uColor}20`,
                    }}
                  >
                    {URGENCY_COLORS[row.urgency]?.label}
                  </span>
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: T.space.xxs,
                      padding: '2px 8px', borderRadius: T.radius.sm,
                      fontSize: T.type.caption, fontWeight: T.weight.semibold, textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background: `${statusInfo?.color || T.color.neutral}12`,
                      color: statusInfo?.color || T.color.neutral,
                    }}
                  >
                    <StatusDot color={statusInfo?.color} size={5} />
                    {statusInfo?.label}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="task-preview-card__expand"
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
            >
              {isExpanded ? 'Less info' : 'More info'}
              <span className={`task-preview-card__chevron${isExpanded ? ' task-preview-card__chevron--open' : ''}`}>
                &#9662;
              </span>
            </button>
            {isExpanded && (
              <div className="task-preview-card__details">
                <div className="task-preview-card__detail-row">
                  <span className="task-preview-card__detail-label">Assignee</span>
                  <span className="task-preview-card__detail-value">
                    <Avatar initials={row.assignee} size={22} />
                  </span>
                </div>
                <div className="task-preview-card__detail-row">
                  <span className="task-preview-card__detail-label">Due Date</span>
                  <span className="task-preview-card__detail-value">{row.due}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TablePreview = ({ isTablet: isTabletProp }) => {
  const [viewMode, setViewMode] = useState(isTabletProp ? 'cards' : 'table');

  // Auto-switch to cards on tablet/mobile
  useEffect(() => {
    if (isTabletProp) setViewMode('cards');
  }, [isTabletProp]);

  return (
    <Section
      id="sec-11"
      title="11. Table View"
      description="Comfortable and compact density variants with priority borders and status badges."
    >
      {/* View toggle */}
      <div className="task-design-preview__view-toggle">
        <button
          type="button"
          className={`task-design-preview__view-toggle-btn${viewMode === 'table' ? ' task-design-preview__view-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('table')}
          disabled={isTabletProp}
          title={isTabletProp ? 'Table view unavailable at this screen size' : undefined}
        >
          Table
        </button>
        <button
          type="button"
          className={`task-design-preview__view-toggle-btn${viewMode === 'cards' ? ' task-design-preview__view-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('cards')}
        >
          Cards
        </button>
      </div>

      {viewMode === 'table' ? (
        <>
          <SubSection label="Comfortable density (44px rows)">
            <div className="task-design-preview__table-wrap">
              <TaskTable />
            </div>
          </SubSection>
          <SubSection label="Compact density (reduced padding, 12px text)">
            <div className="task-design-preview__table-wrap">
              <TaskTable compact />
            </div>
          </SubSection>
        </>
      ) : (
        <SubSection label="Card view (responsive transformation)">
          <TaskCardView />
        </SubSection>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// 18. Drawer Layout (Patterns)
// ---------------------------------------------------------------------------

const DrawerPreview = () => (
  <Section
    id="sec-18"
    title="18. Drawer Layout"
    description="Embedded drawer showing header, content with sidebar, detail cards, and history timeline."
  >
    <div className="task-design-preview__drawer-embed">
      {/* Header */}
      <div
        className="tm-drawer__header"
        style={{
          padding: `${T.space.md}px ${T.space.lg}px`,
          borderBottom: '1px solid var(--preview-border)',
          background: 'linear-gradient(180deg, var(--preview-card-bg) 0%, var(--preview-glass-strong) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: T.space.xs,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: T.space.sm, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
          <div
            className="tm-drawer__urgency-dot"
            style={{ width: 8, height: 8, borderRadius: '50%', background: T.color.urgencyHigh, boxShadow: '0 0 0 2px var(--preview-card-bg), 0 0 8px rgba(249,115,22,0.3)' }}
          />
          <span style={{ fontSize: T.type.h3, fontWeight: T.weight.semibold, color: C.text, letterSpacing: '-0.02em' }}>
            Design new landing page
          </span>
          <span
            className="tm-drawer__project-tag"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: T.space.xxs,
              padding: `${T.space.xxs}px ${T.space.sm}px`, borderRadius: T.radius.pill, fontSize: T.type.caption,
              fontWeight: T.weight.semibold, background: 'rgba(59,130,246,0.08)', color: T.color.infoDark,
              border: '1px solid rgba(59,130,246,0.15)',
            }}
          >
            Marketing
          </span>
        </div>
        <div style={{ display: 'flex', gap: T.space.xs }}>
          <button
            type="button"
            style={{
              width: T.size.iconBtnMd, height: T.size.iconBtnMd, borderRadius: T.radius.md,
              border: '1px solid ' + C.border, background: 'var(--preview-glass)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: T.type.small, color: C.muted, transition: 'all 180ms ease',
            }}
          >
            &#9998;
          </button>
          <button
            type="button"
            style={{
              width: T.size.iconBtnMd, height: T.size.iconBtnMd, borderRadius: T.radius.md,
              border: '1px solid ' + C.border, background: 'var(--preview-glass)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: T.type.small, color: C.muted, transition: 'all 180ms ease',
            }}
          >
            &times;
          </button>
        </div>
      </div>

      {/* Content: Main + Sidebar */}
      <div
        className="tm-drawer__content task-design-preview__drawer-layout"
        style={{ display: 'flex', gap: T.space.lg, padding: `${T.space.md}px ${T.space.lg}px`, minHeight: 320, flexWrap: 'wrap' }}
      >
        {/* Main area */}
        <div className="tm-drawer__main" style={{ flex: 1, minWidth: 0 }}>
          {/* Description card */}
          <div className="tm-drawer__card" style={{ marginBottom: T.space.md }}>
            <div className="tm-drawer__card-header" style={{ marginBottom: T.space.sm }}>
              <span className="tm-drawer__card-title" style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Description
              </span>
            </div>
            <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.6 }}>
              Create a modern, responsive landing page for the Q1 product launch.
              Include hero section, features grid, testimonials, and CTA.
              Follow the new brand guidelines with updated typography and color palette.
            </div>
          </div>

          {/* History timeline */}
          <div className="tm-drawer__card">
            <div className="tm-drawer__card-header" style={{ marginBottom: T.space.sm }}>
              <span className="tm-drawer__card-title" style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                History
              </span>
            </div>
            <div className="tm-drawer__history">
              <div className="tm-drawer__history-group">
                <div className="tm-drawer__history-date">Feb 14, 2026</div>
                <div className="tm-drawer__history-items">
                  <div className="tm-drawer__history-item tm-drawer__history-item--status">
                    <div className="tm-drawer__history-time">2:30 PM</div>
                    <div className="tm-drawer__history-action">
                      <span className="tm-drawer__history-actor">Alice</span> changed status
                    </div>
                    <div className="tm-drawer__history-change">
                      <span className="tm-drawer__history-old">Not Started</span>
                      <span className="tm-drawer__history-arrow">&rarr;</span>
                      <span className="tm-drawer__history-new">In Progress</span>
                    </div>
                  </div>
                  <div className="tm-drawer__history-item tm-drawer__history-item--assignee">
                    <div className="tm-drawer__history-time">10:15 AM</div>
                    <div className="tm-drawer__history-action">
                      <span className="tm-drawer__history-actor">Bob</span> assigned to Alice
                    </div>
                  </div>
                  <div className="tm-drawer__history-item tm-drawer__history-item--created">
                    <div className="tm-drawer__history-time">9:00 AM</div>
                    <div className="tm-drawer__history-action">
                      <span className="tm-drawer__history-actor">Bob</span> created this task
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="tm-drawer__sidebar task-design-preview__drawer-sidebar" style={{ width: 280, flexShrink: 0 }}>
          {/* Details card */}
          <div className="tm-drawer__card">
            <div className="tm-drawer__card-header" style={{ marginBottom: T.space.sm }}>
              <span className="tm-drawer__card-title" style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Details
              </span>
            </div>
            <div className="tm-drawer__details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${T.space.sm}px ${T.space.md}px` }}>
              <div className="tm-drawer__details-item">
                <span className="tm-drawer__detail-label">Status</span>
                <span className="tm-drawer__detail-value" style={{ display: 'flex', alignItems: 'center', gap: T.space.xs }}>
                  <StatusDot color="#3b82f6" /> In Progress
                </span>
              </div>
              <div className="tm-drawer__details-item">
                <span className="tm-drawer__detail-label">Urgency</span>
                <span className="tm-drawer__detail-value" style={{ display: 'flex', alignItems: 'center', gap: T.space.xs }}>
                  <StatusDot color="#f97316" /> High
                </span>
              </div>
              <div className="tm-drawer__details-item">
                <span className="tm-drawer__detail-label">Due Date</span>
                <span className="tm-drawer__detail-value">Feb 20, 2026</span>
              </div>
              <div className="tm-drawer__details-item">
                <span className="tm-drawer__detail-label">Request Type</span>
                <span className="tm-drawer__detail-value">Design</span>
              </div>
            </div>
          </div>

          {/* Assignees card */}
          <div className="tm-drawer__card">
            <div className="tm-drawer__card-header" style={{ marginBottom: T.space.sm }}>
              <span className="tm-drawer__card-title" style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Assignees
              </span>
            </div>
            <div className="tm-drawer__assignee-display">
              <Avatar initials="AB" size={32} color="var(--ant-primary-color, #1890ff)" />
              <div className="tm-drawer__assignee-info">
                <div className="tm-drawer__assignee-name">Alice Brown</div>
                <div className="tm-drawer__assignee-org">Design Team</div>
              </div>
            </div>
          </div>

          {/* Followers card */}
          <div className="tm-drawer__card">
            <div className="tm-drawer__card-header" style={{ marginBottom: T.space.sm }}>
              <span className="tm-drawer__card-title" style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Followers
              </span>
            </div>
            <div className="tm-drawer__avatar-stack" style={{ display: 'flex', alignItems: 'center' }}>
              <Avatar initials="CD" size={28} color="#26a69a" />
              <Avatar initials="EF" size={28} color="#8b5cf6" />
              <Avatar initials="GH" size={28} color="#f97316" />
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Responsive Drawer — Breakpoint behavior */}
    <SubSection label="Responsive Drawer">
      <p style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.6, marginBottom: T.space.md }}>
        Drawers are right-anchored panels that overlay content. On smaller viewports
        they scale down gracefully, eventually filling the full viewport width on mobile
        to maximize usable space.
      </p>

      {/* Breakpoint table */}
      <div className="tm-drawer-responsive">
        <table className="tm-drawer-responsive__table">
          <thead>
            <tr>
              <th>Breakpoint</th>
              <th>Width</th>
              <th>Padding</th>
              <th>Columns</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>&gt; 992px</code></td>
              <td>Fixed (e.g. 750px)</td>
              <td>24px body, 24px header</td>
              <td>All visible</td>
            </tr>
            <tr>
              <td><code>&le; 992px</code></td>
              <td><code>85vw !important</code></td>
              <td>Same</td>
              <td>All visible</td>
            </tr>
            <tr>
              <td><code>&le; 768px</code></td>
              <td><code>100vw !important</code></td>
              <td>16px body, 16px header</td>
              <td>Hide tertiary columns (e.g. dates)</td>
            </tr>
            <tr>
              <td><code>&le; 576px</code></td>
              <td><code>100vw</code></td>
              <td>12px body, 12px header</td>
              <td>Minimal — primary + status only</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Visual: three viewport boxes */}
      <div className="tm-drawer-responsive__demos">
        {/* Desktop */}
        <div className="tm-drawer-responsive__demo">
          <div className="tm-drawer-responsive__viewport tm-drawer-responsive__viewport--desktop">
            <div className="tm-drawer-responsive__page" />
            <div className="tm-drawer-responsive__panel tm-drawer-responsive__panel--desktop">
              <div className="tm-drawer-responsive__panel-header" />
              <div className="tm-drawer-responsive__panel-row" />
              <div className="tm-drawer-responsive__panel-row" />
              <div className="tm-drawer-responsive__panel-row tm-drawer-responsive__panel-row--dim" />
              <div className="tm-drawer-responsive__panel-row tm-drawer-responsive__panel-row--dim" />
            </div>
          </div>
          <span className="tm-drawer-responsive__label">Desktop — 750px</span>
        </div>

        {/* Tablet */}
        <div className="tm-drawer-responsive__demo">
          <div className="tm-drawer-responsive__viewport tm-drawer-responsive__viewport--tablet">
            <div className="tm-drawer-responsive__page" />
            <div className="tm-drawer-responsive__panel tm-drawer-responsive__panel--tablet">
              <div className="tm-drawer-responsive__panel-header" />
              <div className="tm-drawer-responsive__panel-row" />
              <div className="tm-drawer-responsive__panel-row" />
              <div className="tm-drawer-responsive__panel-row tm-drawer-responsive__panel-row--dim" />
              <div className="tm-drawer-responsive__panel-row tm-drawer-responsive__panel-row--dim" />
            </div>
          </div>
          <span className="tm-drawer-responsive__label">Tablet — 85vw</span>
        </div>

        {/* Mobile */}
        <div className="tm-drawer-responsive__demo">
          <div className="tm-drawer-responsive__viewport tm-drawer-responsive__viewport--mobile">
            <div className="tm-drawer-responsive__panel tm-drawer-responsive__panel--mobile">
              <div className="tm-drawer-responsive__panel-header" />
              <div className="tm-drawer-responsive__panel-row" />
              <div className="tm-drawer-responsive__panel-row" />
            </div>
          </div>
          <span className="tm-drawer-responsive__label">Mobile — 100vw</span>
        </div>
      </div>

      <p style={{ fontSize: T.type.caption, color: C.muted, lineHeight: 1.5, marginTop: T.space.sm }}>
        <strong>Key rules:</strong> Override AntD inline <code>width</code> with <code>!important</code>.
        Remove glass border-left at 100vw. Hide non-essential table columns on mobile.
        Close button gets 44px touch target. Compact pagination stacks to single column.
      </p>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 1. Colors & Palette (Foundations)
// ---------------------------------------------------------------------------

const SwatchGroup = ({ title, colors }) => (
  <SubSection label={title}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.sm, marginBottom: T.space.lg }}>
      {colors.map(({ hex, name }) => (
        <div key={name} style={{ textAlign: 'center', width: 66, cursor: 'default', transition: 'transform 180ms ease' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: T.radius.input,
              background: hex,
              border: '1px solid var(--preview-border)',
              margin: `0 auto ${T.space.xs}px`,
              boxShadow: '0 1px 3px var(--preview-border)',
              transition: 'transform 180ms ease, box-shadow 180ms ease',
            }}
          />
          <div style={{ fontSize: T.type.caption, color: C.label, lineHeight: 1.2, fontWeight: T.weight.medium }}>{name}</div>
          <div style={{ fontSize: T.type.caption, color: C.muted, fontFamily: C.mono, marginTop: 2, letterSpacing: '0.01em' }}>{hex}</div>
        </div>
      ))}
    </div>
  </SubSection>
);

const TokenSwatchPreview = ({ darkMode }) => (
  <Section
    id="sec-1"
    title="1. Colors & Palette"
    description="Color palette, urgency, status, neutral swatches, and semantic text colors."
  >
    <SwatchGroup
      title="Primary & Secondary"
      colors={[
        { hex: '#1890ff', name: 'Primary' },
        { hex: '#40a9ff', name: 'Pri Light' },
        { hex: '#096dd9', name: 'Pri Dark' },
        { hex: '#26a69a', name: 'Secondary' },
        { hex: '#4db6ac', name: 'Sec Light' },
        { hex: '#00897b', name: 'Sec Dark' },
      ]}
    />
    <SwatchGroup
      title="Urgency"
      colors={[
        { hex: '#dc2626', name: 'Critical' },
        { hex: '#f97316', name: 'High' },
        { hex: '#eab308', name: 'Medium' },
        { hex: '#22c55e', name: 'Low' },
      ]}
    />
    <SwatchGroup
      title="Status"
      colors={[
        { hex: '#94a3b8', name: 'Not Started' },
        { hex: '#1890ff', name: 'Triaged' },
        { hex: '#13c2c2', name: 'Ready for Review' },
        { hex: '#3b82f6', name: 'In Progress' },
        { hex: '#dc2626', name: 'Blocked' },
        { hex: '#8b5cf6', name: 'On Hold' },
        { hex: '#22c55e', name: 'Completed' },
        { hex: '#722ed1', name: 'Monitoring' },
        { hex: '#8c8c8c', name: 'Duplicate' },
        { hex: '#fa8c16', name: 'Input Required' },
        { hex: '#8c8c8c', name: 'No Longer Needed' },
      ]}
    />
    <SwatchGroup
      title="Neutral Scale"
      colors={[
        { hex: '#fafafa', name: '25' },
        { hex: '#f5f5f5', name: '50' },
        { hex: '#eeeeee', name: '100' },
        { hex: '#e0e0e0', name: '200' },
        { hex: '#bdbdbd', name: '300' },
        { hex: '#9e9e9e', name: '400' },
        { hex: '#757575', name: '500' },
        { hex: '#616161', name: '600' },
        { hex: '#424242', name: '700' },
        { hex: '#212121', name: '800' },
        { hex: '#121212', name: '900' },
        { hex: '#0a0a0a', name: '950' },
      ]}
    />

    <SubSection label="Sidebar color preview">
      <div style={{
        display: 'flex', gap: T.space.lg, flexWrap: 'wrap', alignItems: 'flex-start',
      }}>
        {/* Mini sidebar mock */}
        <div style={{
          width: 220, borderRadius: T.radius.lg, overflow: 'hidden',
          background: 'var(--mj-sidebar-color, #001529)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          fontFamily: C.sans,
        }}>
          {/* Sidebar header */}
          <div style={{
            padding: `${T.space.md}px ${T.space.md}px ${T.space.sm}px`,
            borderBottom: `1px solid ${sidebarRgba(0.15)}`,
          }}>
            <div style={{
              fontSize: T.type.small, fontWeight: T.weight.semibold, color: 'rgba(255,255,255,0.95)',
              letterSpacing: '-0.01em',
            }}>
              MediaJel
            </div>
            <div style={{ fontSize: T.type.caption, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
              Admin Dashboard
            </div>
          </div>
          {/* Menu items */}
          <div style={{ padding: `${T.space.xs}px 0` }}>
            {[
              { label: 'Dashboard', active: false },
              { label: 'Tasks', active: true },
              { label: 'Campaigns', active: false },
              { label: 'Analytics', active: false },
              { label: 'Settings', active: false },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: `${T.space.xs}px ${T.space.md}px`,
                  fontSize: T.type.small,
                  fontWeight: item.active ? T.weight.semibold : T.weight.regular,
                  color: item.active ? T.color.onDark : 'rgba(255,255,255,0.65)',
                  background: item.active ? primaryRgba(0.2) : 'transparent',
                  borderRight: item.active ? `3px solid var(--ant-primary-color, #1890ff)` : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 180ms ease',
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
        {/* Token info */}
        <div style={{ flex: '1 1 200px', minWidth: 180 }}>
          <div style={{ fontSize: T.type.caption, color: C.secondary, marginBottom: T.space.sm, lineHeight: 1.6 }}>
            The sidebar uses <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>--mj-sidebar-color</code> and the active menu item highlights with the primary color.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xs }}>
            {[
              { label: '--mj-sidebar-color', desc: 'Sidebar background' },
              { label: '--sidebar-red/green/blue', desc: 'RGB channels for rgba()' },
              { label: '--ant-primary-color', desc: 'Active item accent' },
            ].map((tok) => (
              <div key={tok.label} style={{
                padding: `${T.space.xs}px ${T.space.sm}px`, background: 'var(--preview-glass-subtle)',
                borderRadius: T.radius.md, border: '1px solid ' + C.border,
              }}>
                <code style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.body, fontWeight: T.weight.medium }}>{tok.label}</code>
                <div style={{ fontSize: T.type.caption, color: C.muted, marginTop: 2 }}>{tok.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubSection>
    <TextColorsContent darkMode={darkMode} />
  </Section>
);

// ---------------------------------------------------------------------------
// 2. Typography (Foundations)
// ---------------------------------------------------------------------------

// ── Governed Type Scale (8 sizes) ──────────────────────────────────────
// Every font-size in the system resolves to one of these values.
const FONT_SIZES = [
  { token: '$type-caption',  px: 12, usage: 'Tiny labels, badges, timestamps, compact UI' },
  { token: '$type-small',    px: 13, usage: 'Secondary text, table cells, input fields' },
  { token: '$type-button',   px: 14, usage: 'Button labels, interactive controls' },
  { token: '$type-body',     px: 15, usage: 'Primary body text (base)' },
  { token: '$type-body-lg',  px: 16, usage: 'Section sub-headings, empty-state titles' },
  { token: '$type-h3',       px: 18, usage: 'Drawer headings, card titles' },
  { token: '$type-h2',       px: 22, usage: 'Page titles, section headings' },
  { token: '$type-h1',       px: 28, usage: 'Hero headings, large display text' },
];

const FONT_WEIGHTS = [
  { token: '$font-weight-regular',   value: 400, label: 'Regular' },
  { token: '$font-weight-medium',    value: 500, label: 'Medium' },
  { token: '$font-weight-semibold',  value: 600, label: 'Semibold' },
];

const LINE_HEIGHTS = [
  { token: '$line-height-tight',   value: 1.25, label: 'Tight (headings)' },
  { token: '$line-height-normal',  value: 1.5,  label: 'Normal' },
  { token: '$line-height-body',    value: 1.6,  label: 'Body (governed)' },
  { token: '$line-height-relaxed', value: 1.625, label: 'Relaxed' },
];

const LETTER_SPACINGS = [
  { token: '$letter-spacing-tighter', value: '-0.05em', label: 'Tighter' },
  { token: '$letter-spacing-tight',   value: '-0.025em', label: 'Tight' },
  { token: '$letter-spacing-normal',  value: '0',        label: 'Normal' },
  { token: '$letter-spacing-wide',    value: '0.025em',  label: 'Wide' },
  { token: '$letter-spacing-wider',   value: '0.05em',   label: 'Wider' },
  { token: '$letter-spacing-widest',  value: '0.1em',    label: 'Widest' },
];

const tokenLabelStyle = {
  fontSize: T.type.caption,
  fontFamily: C.mono,
  color: C.muted,
  marginTop: T.space.xxs,
  letterSpacing: '0.01em',
};

const TypographyPreview = () => (
  <Section
    id="sec-2"
    title="2. Typography"
    description="Plus Jakarta Sans type scale, weights, line heights, letter spacing, and monospace."
  >
    <SubSection label="Font families">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', padding: `${T.space.md}px ${T.space.lg}px`, background: 'var(--preview-subtle-bg)', borderRadius: T.radius.lg, border: '1px solid ' + C.border }}>
          <div style={{ fontFamily: C.sans, fontSize: T.type.h2, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xs, letterSpacing: '-0.02em' }}>
            Plus Jakarta Sans
          </div>
          <div style={{ fontFamily: C.sans, fontSize: T.type.small, color: C.secondary, marginBottom: T.space.xs }}>Primary typeface — geometric warmth</div>
          <div style={tokenLabelStyle}>$font-family-base</div>
        </div>
        <div style={{ flex: '1 1 300px', padding: `${T.space.md}px ${T.space.lg}px`, background: 'var(--preview-subtle-bg)', borderRadius: T.radius.lg, border: '1px solid ' + C.border }}>
          <div style={{ fontFamily: C.mono, fontSize: T.type.h3, fontWeight: T.weight.medium, color: C.text, marginBottom: T.space.xs }}>
            JetBrains Mono
          </div>
          <div style={{ fontFamily: C.mono, fontSize: T.type.small, color: C.secondary, marginBottom: T.space.xs }}>Code &amp; technical labels</div>
          <div style={tokenLabelStyle}>$font-family-mono</div>
        </div>
      </div>
    </SubSection>

    <SubSection label="Font weights">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.sm }}>
        {FONT_WEIGHTS.map((fw) => (
          <div key={fw.token} className="task-design-preview__type-specimen" style={{ display: 'flex', alignItems: 'baseline', gap: T.space.md }}>
            <div style={{ width: 60, textAlign: 'right', fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, flexShrink: 0 }}>
              {fw.value}
            </div>
            <div style={{
              fontFamily: C.sans,
              fontSize: T.type.bodyLg,
              fontWeight: fw.value,
              color: C.text,
              flex: 1,
            }}>
              {fw.label} — The quick brown fox jumps
            </div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.faint, flexShrink: 0, width: 180, textAlign: 'right' }}>
              {fw.token}
            </div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Line heights">
      <div className="task-design-preview__row" style={{ gap: T.space.md }}>
        {LINE_HEIGHTS.map((lh) => (
          <div key={lh.token} style={{ flex: '1 1 180px', minWidth: 150 }}>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginBottom: T.space.xxs }}>
              {lh.label} ({lh.value})
            </div>
            <div style={{
              fontFamily: C.sans,
              fontSize: T.type.small,
              lineHeight: lh.value,
              color: C.body,
              background: 'var(--preview-subtle-bg)',
              padding: `${T.space.xs}px ${T.space.md}px`,
              borderRadius: T.radius.input,
              border: '1px solid ' + C.border,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor.
            </div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Letter spacing">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.sm }}>
        {LETTER_SPACINGS.map((ls) => (
          <div key={ls.token} className="task-design-preview__type-specimen" style={{ display: 'flex', alignItems: 'center', gap: T.space.md }}>
            <div style={{ width: 80, textAlign: 'right', fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, flexShrink: 0 }}>
              {ls.value}
            </div>
            <div style={{
              fontFamily: C.sans,
              fontSize: T.type.caption,
              fontWeight: T.weight.semibold,
              textTransform: 'uppercase',
              letterSpacing: ls.value,
              color: C.body,
              flex: 1,
            }}>
              {ls.label} — ABCDEFGHIJKLMNOPQRSTUVWXYZ
            </div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Type scale (8 sizes — authoritative)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {TYPE_SCALE_RULES.map((t) => (
          <div key={t.size} className="task-design-preview__type-specimen" style={{ display: 'flex', alignItems: 'baseline', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap', padding: `${T.space.xs}px 0`, borderBottom: '1px solid ' + C.border }}>
            <div style={{ width: 48, textAlign: 'right', fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, flexShrink: 0 }}>{t.size}px</div>
            <div style={{ fontSize: t.size, fontWeight: t.weight, color: C.text, flex: '1 1 200px', minWidth: 0, lineHeight: t.lineHeight }}>
              The quick brown fox
            </div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.label, flexShrink: 0, minWidth: 120 }}>{t.token}</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.secondary, flexShrink: 0, minWidth: 80 }}>{t.weight} / {t.lineHeight}</div>
            <div style={{ fontSize: T.type.caption, color: C.faint, flex: '0 0 100%', marginTop: 2 }}>
              {t.role}
              {t.aliases && <span style={{ fontFamily: C.mono, marginLeft: T.space.xs, color: C.muted }}>aliases: {t.aliases}</span>}
            </div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Typography hard constraints">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Base font size:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>15px ($type-body)</code>. All relative sizing derives from this.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Allowed weights:</strong> 400 (regular) / 500 (medium) / 600 (semibold) only. <span style={{ color: T.color.forbidden }}>300, 700, and 800 are forbidden.</span></div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Body line-height:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>1.6 ($line-height-body)</code>. All body text, descriptions, and comments must use this value.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Heading line-height:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>1.2–1.3 ($line-height-tight: 1.25)</code>. Applies to $type-heading and $type-display.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Uppercase:</strong> Allowed <em>only</em> for caption-level text (12px) with <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>letter-spacing: 0.04em</code>. Never on body or heading text.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Font sizes:</strong> No arbitrary values. Every font-size must resolve to one of the 8 governed sizes: 12 / 13 / 14 / 15 / 16 / 18 / 22 / 28.</div>
        <div><strong style={{ color: C.text }}>Heading letter-spacing:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>-0.02em</code> for display, <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>-0.01em</code> for subheads.</div>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// Text Colors (content-only — merged into Colors & Palette)
// ---------------------------------------------------------------------------

const TEXT_COLORS = [
  { token: '$neutral-900', hex: '#121212', label: 'Headings', sample: 'Page Title' },
  { token: '$neutral-800', hex: '#212121', label: 'Strong text', sample: 'Assignee Name' },
  { token: '$neutral-700', hex: '#424242', label: 'Body text', sample: 'Table cell content and descriptions' },
  { token: '$neutral-600', hex: '#616161', label: 'Ghost button text', sample: 'Ghost action' },
  { token: '$neutral-500', hex: '#757575', label: 'Secondary / labels', sample: 'Detail label, context line' },
  { token: '$neutral-400', hex: '#9e9e9e', label: 'Placeholder / muted', sample: 'Placeholder text...' },
  { token: '$neutral-300', hex: '#bdbdbd', label: 'Disabled text', sample: 'Disabled option' },
  { token: '$task-primary', hex: '#1890ff', label: 'Links / active', sample: 'View details' },
  { token: '$accent-primary-dark', hex: '#096dd9', label: 'Link hover', sample: 'Hovered link' },
  { token: '$status-error-text', hex: '#991b1b', label: 'Error text', sample: 'This field is required' },
  { token: '$status-warning-text', hex: '#854d0e', label: 'Warning text', sample: 'Due date approaching' },
  { token: '$status-success-text', hex: '#166534', label: 'Success text', sample: 'Task completed!' },
  { token: '$link-blue-text', hex: '#1e40af', label: 'Project tag text', sample: 'Marketing Campaign' },
];

const TEXT_COLORS_DARK = [
  { token: '$neutral-900', hex: '#fafafa', label: 'Headings', sample: 'Page Title' },
  { token: '$neutral-800', hex: '#e4e4e7', label: 'Strong text', sample: 'Assignee Name' },
  { token: '$neutral-700', hex: '#a1a1aa', label: 'Body text', sample: 'Table cell content and descriptions' },
  { token: '$neutral-600', hex: '#a1a1aa', label: 'Ghost button text', sample: 'Ghost action' },
  { token: '$neutral-500', hex: '#71717a', label: 'Secondary / labels', sample: 'Detail label, context line' },
  { token: '$neutral-400', hex: '#52525b', label: 'Placeholder / muted', sample: 'Placeholder text...' },
  { token: '$neutral-300', hex: '#3f3f46', label: 'Disabled text', sample: 'Disabled option' },
  { token: '$task-primary', hex: '#1890ff', label: 'Links / active', sample: 'View details' },
  { token: '$accent-primary-dark', hex: '#40a9ff', label: 'Link hover', sample: 'Hovered link' },
  { token: '$status-error-text', hex: '#fca5a5', label: 'Error text', sample: 'This field is required' },
  { token: '$status-warning-text', hex: '#fcd34d', label: 'Warning text', sample: 'Due date approaching' },
  { token: '$status-success-text', hex: '#86efac', label: 'Success text', sample: 'Task completed!' },
  { token: '$link-blue-text', hex: '#93c5fd', label: 'Project tag text', sample: 'Marketing Campaign' },
];

const TextColorsContent = ({ darkMode }) => {
  const colors = darkMode ? TEXT_COLORS_DARK : TEXT_COLORS;
  return (
    <SubSection label="Text colors">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xs }}>
        {colors.map((tc) => (
          <div key={tc.token} className="task-design-preview__token-row" style={{
            display: 'flex', alignItems: 'center', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap',
            padding: `${T.space.xs}px ${T.space.md}px`, borderRadius: T.radius.input,
            background: 'var(--preview-card-bg)', border: '1px solid ' + C.border,
            transition: 'all 180ms ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: T.radius.md, background: tc.hex,
              border: '1px solid var(--preview-border)', flexShrink: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }} />
            <div style={{ width: 80, fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, flexShrink: 0, letterSpacing: '0.01em' }}>
              {tc.hex}
            </div>
            <div style={{
              fontFamily: C.sans,
              fontSize: T.type.small,
              color: tc.hex,
              fontWeight: tc.hex === '#121212' || tc.hex === '#212121' ? 600 : 400,
              flex: '1 1 120px',
              minWidth: 0,
            }}>
              {tc.sample}
            </div>
            <div style={{ fontSize: T.type.caption, color: C.faint }}>
              {tc.label} &middot; <span style={{ fontFamily: C.mono }}>{tc.token}</span>
            </div>
          </div>
        ))}
      </div>
    </SubSection>
  );
};

// ---------------------------------------------------------------------------
// 4. Border Radius (Foundations)
// ---------------------------------------------------------------------------

const RADII = [
  { token: '$radius-none',  px: 0,    label: 'None',  usage: 'No rounding' },
  { token: '$radius-xs',    px: 4,    label: 'XS',    usage: 'Inline indicators, skeletons' },
  { token: '$radius-sm',    px: 6,    label: 'SM',    usage: 'Small buttons, banners' },
  { token: '$radius-md',    px: 8,    label: 'MD',    usage: 'Cards, dropdowns, badges' },
  { token: '$radius-input', px: 10,   label: 'Input', usage: 'Form inputs, selects' },
  { token: '$radius-lg',    px: 12,   label: 'LG',    usage: 'Buttons, modals, popovers' },
  { token: '$radius-xl',    px: 16,   label: 'XL',    usage: 'Large cards, drawers' },
  { token: '$radius-2xl',   px: 20,   label: '2XL',   usage: 'Feature sections' },
  { token: '$radius-3xl',   px: 24,   label: '3XL',   usage: 'Hero elements' },
  { token: '$radius-full',  px: 9999, label: 'Full',  usage: 'Pills, avatars, dots' },
];

const BorderRadiusPreview = () => (
  <Section
    id="sec-4"
    title="4. Border Radius"
    description="Radius scale from sharp corners to full pills, shown at actual size."
  >
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.lg, alignItems: 'flex-end' }}>
      {RADII.map((r) => {
        const displayRadius = r.px > 100 ? '50%' : r.px;
        return (
          <div key={r.token} style={{ textAlign: 'center', width: 76, transition: 'transform 180ms ease' }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: displayRadius,
              background: `linear-gradient(135deg, ${primaryRgba(0.85)} 0%, ${primaryRgba(0.6)} 100%)`,
              margin: `0 auto ${T.space.xs}px`,
              boxShadow: `0 2px 8px ${primaryRgba(0.2)}`,
              transition: 'transform 180ms ease, box-shadow 180ms ease',
            }} />
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.body }}>{r.label}</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginTop: 2 }}>
              {r.px === 9999 ? '9999px' : `${r.px}px`}
            </div>
            <div style={{ fontSize: T.type.caption, color: C.faint, marginTop: 2 }}>{r.usage}</div>
          </div>
        );
      })}
    </div>

    <SubSection label="Applied examples">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', marginTop: T.space.md }}>
        {[
          { r: 4, label: '4px — Inline indicator' },
          { r: 8, label: '8px — Card / badge' },
          { r: 10, label: '10px — Form input' },
          { r: 12, label: '12px — Button / modal' },
          { r: 16, label: '16px — Drawer / large card' },
          { r: 9999, label: '9999px — Pill' },
        ].map((ex) => (
          <div key={ex.r} style={{
            padding: `${T.space.xs}px ${T.space.md}px`, borderRadius: ex.r,
            border: '1px solid ' + C.border, background: 'var(--preview-card-bg)',
            fontSize: T.type.caption, color: C.body, fontWeight: T.weight.medium,
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            transition: 'all 180ms ease',
          }}>
            {ex.label}
          </div>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 5. Elevation & Shadows (Foundations)
// ---------------------------------------------------------------------------

const SHADOWS = [
  { token: '$shadow-xs',         value: '0 1px 2px rgba(0,0,0,0.04)',                                          label: 'XS',         usage: 'Minimal lift' },
  { token: '$shadow-sm',         value: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',             label: 'SM',         usage: 'Subtle cards, avatars' },
  { token: '$shadow-md',         value: '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)',             label: 'MD',         usage: 'Hover lift, elevated' },
  { token: '$shadow-lg',         value: '0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.03)',           label: 'LG',         usage: 'Dropdowns' },
  { token: '$shadow-xl',         value: '0 20px 25px rgba(0,0,0,0.06), 0 8px 10px rgba(0,0,0,0.04)',          label: 'XL',         usage: 'Modals' },
  { token: '$shadow-card',       value: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',            label: 'Card',       usage: 'Kanban card default' },
  { token: '$shadow-card-hover', value: '0 8px 24px rgba(0,0,0,0.12), 0 16px 32px rgba(0,0,0,0.08)',          label: 'Card Hover', usage: 'Card hover/focus' },
  { token: '$shadow-card-drag',  value: '0 16px 32px rgba(0,0,0,0.16), 0 32px 64px rgba(0,0,0,0.12)',         label: 'Card Drag',  usage: 'Dragging state' },
  { token: '$shadow-overlay',    value: '0 8px 32px rgba(0,0,0,0.12), 0 24px 64px rgba(0,0,0,0.08)',          label: 'Overlay',    usage: 'Modal overlay' },
  { token: '$shadow-drawer',     value: '-8px 0 32px rgba(0,0,0,0.08), -24px 0 64px rgba(0,0,0,0.05)',        label: 'Drawer',     usage: 'Drawer panel' },
];

const ACCENT_SHADOWS = [
  { token: '$shadow-primary-sm', value: '0 1px 3px rgba(24,144,255,0.15)',   label: 'Primary SM' },
  { token: '$shadow-primary-md', value: '0 2px 6px rgba(24,144,255,0.15)',   label: 'Primary MD' },
  { token: '$shadow-primary-lg', value: '0 4px 12px rgba(24,144,255,0.15)',  label: 'Primary LG' },
  { token: '$shadow-cyan-sm',   value: '0 1px 3px rgba(38,166,154,0.15)',   label: 'Cyan SM' },
  { token: '$shadow-cyan-md',   value: '0 4px 16px rgba(38,166,154,0.15)',  label: 'Cyan MD' },
];

const FOCUS_SHADOWS = [
  { token: '$shadow-focus',     value: '0 0 0 3px rgba(24,144,255,0.08), 0 0 0 1px #1890ff', label: 'Focus Ring' },
  { token: '$shadow-selection', value: '0 0 0 4px rgba(24,144,255,0.12)',                     label: 'Selection Glow' },
];

const SHADOWS_DARK = [
  { token: '$shadow-xs',         value: '0 1px 2px rgba(0,0,0,0.3)',                                              label: 'XS',         usage: 'Minimal lift' },
  { token: '$shadow-sm',         value: '0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',                   label: 'SM',         usage: 'Subtle cards, avatars' },
  { token: '$shadow-md',         value: '0 4px 6px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.25)',                 label: 'MD',         usage: 'Hover lift, elevated' },
  { token: '$shadow-lg',         value: '0 10px 15px rgba(0,0,0,0.35), 0 4px 6px rgba(0,0,0,0.2)',                label: 'LG',         usage: 'Dropdowns' },
  { token: '$shadow-xl',         value: '0 20px 25px rgba(0,0,0,0.4), 0 8px 10px rgba(0,0,0,0.3)',                label: 'XL',         usage: 'Modals' },
  { token: '$shadow-card',       value: '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',                  label: 'Card',       usage: 'Kanban card default' },
  { token: '$shadow-card-hover', value: '0 8px 24px rgba(0,0,0,0.5), 0 16px 32px rgba(0,0,0,0.35)',               label: 'Card Hover', usage: 'Card hover/focus' },
  { token: '$shadow-card-drag',  value: '0 16px 32px rgba(0,0,0,0.5), 0 32px 64px rgba(0,0,0,0.4)',               label: 'Card Drag',  usage: 'Dragging state' },
  { token: '$shadow-overlay',    value: '0 8px 32px rgba(0,0,0,0.5), 0 24px 64px rgba(0,0,0,0.35)',               label: 'Overlay',    usage: 'Modal overlay' },
  { token: '$shadow-drawer',     value: '-8px 0 32px rgba(0,0,0,0.4), -24px 0 64px rgba(0,0,0,0.3)',              label: 'Drawer',     usage: 'Drawer panel' },
];

const ACCENT_SHADOWS_DARK = [
  { token: '$shadow-primary-sm', value: '0 1px 3px rgba(24,144,255,0.25)',   label: 'Primary SM' },
  { token: '$shadow-primary-md', value: '0 2px 6px rgba(24,144,255,0.3)',    label: 'Primary MD' },
  { token: '$shadow-primary-lg', value: '0 4px 12px rgba(24,144,255,0.3)',   label: 'Primary LG' },
  { token: '$shadow-cyan-sm',   value: '0 1px 3px rgba(38,166,154,0.25)',   label: 'Cyan SM' },
  { token: '$shadow-cyan-md',   value: '0 4px 16px rgba(38,166,154,0.3)',   label: 'Cyan MD' },
];

const FOCUS_SHADOWS_DARK = [
  { token: '$shadow-focus',     value: '0 0 0 3px rgba(24,144,255,0.2), 0 0 0 1px #1890ff', label: 'Focus Ring' },
  { token: '$shadow-selection', value: '0 0 0 4px rgba(24,144,255,0.25)',                    label: 'Selection Glow' },
];

const ShadowsPreview = ({ darkMode }) => {
  const shadows = darkMode ? SHADOWS_DARK : SHADOWS;
  const accents = darkMode ? ACCENT_SHADOWS_DARK : ACCENT_SHADOWS;
  const focus = darkMode ? FOCUS_SHADOWS_DARK : FOCUS_SHADOWS;
  return (
  <Section
    id="sec-5"
    title="5. Elevation & Shadows"
    description="Shadow tokens, surface hierarchy, and 6-level elevation contract. Every box-shadow must resolve to a governed token."
  >
    <SubSection label="Shadow scale">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.lg, alignItems: 'flex-start' }}>
        {shadows.map((s) => (
          <div key={s.token} style={{ textAlign: 'center', width: 100, transition: 'transform 180ms ease' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: T.radius.lg,
              background: 'var(--preview-card-bg)',
              boxShadow: s.value,
              margin: `0 auto ${T.space.sm}px`,
              border: '1px solid var(--preview-subtle-border)',
              transition: 'transform 180ms ease',
            }} />
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.body }}>{s.label}</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginTop: T.space.xxs }}>
              {s.token}
            </div>
            <div style={{ fontSize: T.type.caption, color: C.faint, marginTop: 2 }}>{s.usage}</div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Accent shadows">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.lg }}>
        {accents.map((s) => (
          <div key={s.token} style={{ textAlign: 'center', width: 90 }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: T.radius.lg,
              background: 'var(--preview-card-bg)',
              boxShadow: s.value,
              margin: `0 auto ${T.space.xs}px`,
              border: `1px solid ${primaryRgba(0.1)}`,
            }} />
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.body }}>{s.label}</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.faint }}>{s.token}</div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Focus & selection">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.lg }}>
        {focus.map((s) => (
          <div key={s.token} style={{ textAlign: 'center', width: 120 }}>
            <div style={{
              width: 80,
              height: 40,
              borderRadius: T.radius.lg,
              background: 'var(--preview-card-bg)',
              boxShadow: s.value,
              margin: `0 auto ${T.space.xs}px`,
              border: '1px solid var(--ant-primary-color, #1890ff)',
            }} />
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.body }}>{s.label}</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.faint }}>{s.token}</div>
          </div>
        ))}
      </div>
    </SubSection>

    {/* Surfaces (merged from Surface System) */}
    <SurfaceContent darkMode={darkMode} />

    {/* Elevation contract (merged from Elevation & Shadow Contract) */}
    <ElevationContent />
  </Section>
  );
};

// ---------------------------------------------------------------------------
// Surface System (content-only — merged into Shadows & Elevation)
// ---------------------------------------------------------------------------

const SURFACE_LEVELS = [
  {
    level: 'Level 1',
    name: 'Base',
    modifier: 'base',
    tokens: '$bg-page-gradient + $bg-mesh',
    usage: 'Page canvas, full-bleed backgrounds',
  },
  {
    level: 'Level 2',
    name: 'Container',
    modifier: 'container',
    tokens: '$bg-container (#f4f5f7)',
    usage: 'Kanban columns, sidebar panels',
  },
  {
    level: 'Level 3',
    name: 'Elevated',
    modifier: 'elevated',
    tokens: '$bg-card + $shadow-card',
    usage: 'Cards, drawers, popovers',
  },
  {
    level: 'Level 4',
    name: 'Overlay',
    modifier: 'overlay',
    tokens: '$bg-glass + backdrop-filter',
    usage: 'Modals, tooltips, floating panels',
  },
];

const SURFACE_LEVELS_DARK = [
  {
    level: 'Level 1',
    name: 'Base',
    modifier: 'base',
    tokens: 'gradient(#09090b → #18181b)',
    usage: 'Page canvas, full-bleed backgrounds',
  },
  {
    level: 'Level 2',
    name: 'Container',
    modifier: 'container',
    tokens: '#18181b (zinc-900)',
    usage: 'Kanban columns, sidebar panels',
  },
  {
    level: 'Level 3',
    name: 'Elevated',
    modifier: 'elevated',
    tokens: '#1c1c1e + deep shadow',
    usage: 'Cards, drawers, popovers',
  },
  {
    level: 'Level 4',
    name: 'Overlay',
    modifier: 'overlay',
    tokens: 'rgba(20,20,20,0.85) + blur',
    usage: 'Modals, tooltips, floating panels',
  },
];

const SurfaceContent = ({ darkMode }) => {
  const levels = darkMode ? SURFACE_LEVELS_DARK : SURFACE_LEVELS;
  return (
    <>
      <SubSection label="Surface hierarchy">
        <div className="task-design-preview__surfaces">
          {levels.map((s) => (
            <div
              key={s.modifier}
              className={`task-design-preview__surface-card task-design-preview__surface-card--${s.modifier}`}
            >
              <div className="task-design-preview__surface-level">{s.level}</div>
              <div className="task-design-preview__surface-name">{s.name}</div>
              <div className="task-design-preview__surface-tokens">{s.tokens}</div>
            </div>
          ))}
        </div>
      </SubSection>
      <SubSection label="Surface usage">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: T.space.sm }}>
          {levels.map((s) => (
            <div key={s.modifier} style={{
              padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
              borderRadius: T.radius.input, fontSize: T.type.caption,
              border: '1px solid ' + C.border,
            }}>
              <div style={{ fontWeight: T.weight.semibold, color: C.body, marginBottom: T.space.xxs }}>{s.level} &mdash; {s.name}</div>
              <div style={{ color: C.secondary, fontSize: T.type.caption }}>{s.usage}</div>
            </div>
          ))}
        </div>
      </SubSection>
    </>
  );
};

// ---------------------------------------------------------------------------
// 6. Motion & Timing (Foundations)
// ---------------------------------------------------------------------------

const TIMING_TOKENS = [
  { name: 'Micro',  duration: '80ms',  easing: '$ease-contemporary', token: '$duration-micro',   style: { transitionDuration: '80ms',  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } },
  { name: 'Fast',   duration: '120ms', easing: '$ease-contemporary', token: '$duration-fast',    style: { transitionDuration: '120ms', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } },
  { name: 'Base',   duration: '180ms', easing: '$ease-contemporary', token: '$duration-base',    style: { transitionDuration: '180ms', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } },
  { name: 'Slow',   duration: '280ms', easing: '$ease-contemporary', token: '$duration-slow',    style: { transitionDuration: '280ms', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } },
  { name: 'Slower', duration: '400ms', easing: '$ease-contemporary', token: '$duration-slower',  style: { transitionDuration: '400ms', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' } },
];

const TransitionsPreview = () => (
  <Section
    id="sec-6"
    title="6. Motion & Timing"
    description="5 timing tokens calibrated for a contemporary feel. Hover each row to see the animated ball travel across the track."
  >
    {TIMING_TOKENS.map((t) => (
      <div key={t.token} className="task-design-preview__timing-row">
        <div className="task-design-preview__timing-label">{t.name}</div>
        <div className="task-design-preview__timing-meta">{t.duration} &middot; {t.easing}</div>
        <div className="task-design-preview__timing-track">
          <div
            className="task-design-preview__timing-ball"
            style={t.style}
          />
        </div>
      </div>
    ))}
    <SubSection label="Composed transitions">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', marginTop: T.space.xs }}>
        {[
          { label: '$transition-colors', desc: 'color, background-color, border-color' },
          { label: '$transition-shadow', desc: 'box-shadow' },
          { label: '$transition-transform', desc: 'transform' },
          { label: '$transition-card', desc: 'transform + box-shadow + bg + border' },
        ].map((c) => (
          <div key={c.label} style={{
            padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
            borderRadius: T.radius.input, flex: '1 1 200px',
            border: '1px solid ' + C.border,
            transition: 'all 180ms ease',
          }}>
            <code style={{ fontSize: T.type.caption, color: C.body, fontFamily: C.mono, fontWeight: T.weight.medium }}>{c.label}</code>
            <div style={{ fontSize: T.type.caption, color: C.secondary, marginTop: T.space.xxs }}>{c.desc}</div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Motion governance">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Single easing curve:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$ease-contemporary: cubic-bezier(0.22, 1, 0.36, 1)</code>. All transitions must use this curve.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>5 durations only:</strong> 80ms (micro) / 120ms (fast) / 180ms (base) / 280ms (slow) / 400ms (slower). No arbitrary values.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Default:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>all 180ms $ease-contemporary</code> for hover/focus/active states.</div>
        <div><strong style={{ color: C.text }}>Springs:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$ease-spring</code> deprecated for general use — reserved for drag-and-drop only.</div>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 7. Buttons & Interactive Elements (Components)
// ---------------------------------------------------------------------------

const ButtonPreview = () => (
  <Section
    id="sec-7"
    title="7. Buttons & Interactive Elements"
    description="Primary, secondary, ghost, and pill button variants using token-based mixins."
  >
    <SubSection label="Standard buttons">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="tm-btn tm-btn--primary" type="button">
          Primary Button
        </button>
        <button className="tm-btn tm-btn--secondary" type="button">
          Secondary
        </button>
        <button className="tm-btn tm-btn--ghost" type="button">
          Ghost
        </button>
        <button className="tm-btn tm-btn--danger" type="button">
          Danger
        </button>
        <button className="tm-btn tm-btn--icon" type="button" title="Icon button">
          &#x2715;
        </button>
        <button className="tm-btn tm-btn--primary" type="button" disabled>
          Disabled
        </button>
      </div>
    </SubSection>
    <SubSection label="Status badges">
      <div style={{ display: 'flex', gap: T.space.xs, flexWrap: 'wrap' }}>
        {STATUS_LIST.map((s) => (
          <span
            key={s.key}
            className={`task-table__inline-status task-table__inline-status--${s.key}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: T.space.xxs,
              padding: `${T.space.xxs}px ${T.space.sm}px`, borderRadius: T.radius.sm, fontSize: T.type.caption,
              fontWeight: T.weight.semibold, textTransform: 'uppercase', letterSpacing: '0.04em',
              background: `${s.color}12`, color: s.color,
              transition: 'all 180ms ease',
            }}
          >
            <StatusDot color={s.color} size={6} />
            {s.label}
          </span>
        ))}
      </div>
    </SubSection>
    <SubSection label="Urgency badges">
      <div style={{ display: 'flex', gap: T.space.xs, flexWrap: 'wrap' }}>
        {Object.entries(URGENCY_COLORS).map(([key, { hex, label }]) => (
          <span
            key={key}
            className="task-table__urgency"
            style={{
              display: 'inline-block', padding: `${T.space.xxs}px ${T.space.sm}px`, borderRadius: T.radius.sm,
              fontSize: T.type.caption, fontWeight: T.weight.semibold, textTransform: 'uppercase', letterSpacing: '0.04em',
              background: `${hex}10`, color: hex, border: `1px solid ${hex}20`,
              transition: 'all 180ms ease',
            }}
          >
            {label}
          </span>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 10. Badge System (Components)
// ---------------------------------------------------------------------------

const BadgeSystemPreview = () => (
  <Section
    id="sec-10"
    title="10. Badge System"
    description="Status badges, tags, count badges, avatar badges, and priority tags using BEM classes from _badge.scss."
  >
    <SubSection label="Status badges (dot + text)">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="task-badge task-badge--todo">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Not Started</span>
        </span>
        <span className="task-badge task-badge--triaged">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Triaged</span>
        </span>
        <span className="task-badge task-badge--review">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Ready for Review</span>
        </span>
        <span className="task-badge task-badge--in-progress">
          <span className="task-badge__dot" />
          <span className="task-badge__text">In Progress</span>
        </span>
        <span className="task-badge task-badge--blocked">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Blocked</span>
        </span>
        <span className="task-badge task-badge--on-hold">
          <span className="task-badge__dot" />
          <span className="task-badge__text">On Hold</span>
        </span>
        <span className="task-badge task-badge--done">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Completed</span>
        </span>
        <span className="task-badge task-badge--monitoring">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Monitoring</span>
        </span>
        <span className="task-badge task-badge--duplicate">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Duplicate</span>
        </span>
        <span className="task-badge task-badge--input-required">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Input Required</span>
        </span>
        <span className="task-badge task-badge--not-needed">
          <span className="task-badge__dot" />
          <span className="task-badge__text">No Longer Needed</span>
        </span>
      </div>
    </SubSection>
    <SubSection label="Status badges — small variant">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="task-badge task-badge--sm task-badge--todo">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Not Started</span>
        </span>
        <span className="task-badge task-badge--sm task-badge--in-progress">
          <span className="task-badge__dot" />
          <span className="task-badge__text">In Progress</span>
        </span>
        <span className="task-badge task-badge--sm task-badge--done">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Completed</span>
        </span>
        <span className="task-badge task-badge--sm task-badge--monitoring">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Monitoring</span>
        </span>
        <span className="task-badge task-badge--sm task-badge--not-needed">
          <span className="task-badge__dot" />
          <span className="task-badge__text">No Longer Needed</span>
        </span>
      </div>
    </SubSection>
    <SubSection label="Status badges — large variant">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="task-badge task-badge--lg task-badge--in-progress">
          <span className="task-badge__dot" />
          <span className="task-badge__text">In Progress</span>
        </span>
        <span className="task-badge task-badge--lg task-badge--review">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Ready for Review</span>
        </span>
        <span className="task-badge task-badge--lg task-badge--input-required">
          <span className="task-badge__dot" />
          <span className="task-badge__text">Input Required</span>
        </span>
      </div>
    </SubSection>
    <SubSection label="Tag badges (pill style)">
      <div style={{ display: 'flex', gap: T.space.xs, flexWrap: 'wrap' }}>
        <span className="task-tag task-tag--todo">Not Started</span>
        <span className="task-tag task-tag--triaged">Triaged</span>
        <span className="task-tag task-tag--review">Ready for Review</span>
        <span className="task-tag task-tag--in-progress">In Progress</span>
        <span className="task-tag task-tag--blocked">Blocked</span>
        <span className="task-tag task-tag--on-hold">On Hold</span>
        <span className="task-tag task-tag--completed">Completed</span>
        <span className="task-tag task-tag--monitoring">Monitoring</span>
        <span className="task-tag task-tag--duplicate">Duplicate</span>
        <span className="task-tag task-tag--input-required">Input Required</span>
        <span className="task-tag task-tag--not-needed">No Longer Needed</span>
      </div>
    </SubSection>
    <SubSection label="Priority tags">
      <div style={{ display: 'flex', gap: T.space.xs, flexWrap: 'wrap' }}>
        <span className="task-priority task-priority--low">Low</span>
        <span className="task-priority task-priority--medium">Medium</span>
        <span className="task-priority task-priority--high">High</span>
        <span className="task-priority task-priority--critical">Critical</span>
      </div>
    </SubSection>
    <SubSection label="Count badges">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: T.type.caption, color: C.secondary }}>Default:</span>
        <span className="task-count-badge">5</span>
        <span className="task-count-badge">12</span>
        <span className="task-count-badge">99+</span>
        <span style={{ fontSize: T.type.caption, color: C.secondary, marginLeft: T.space.xs }}>Neutral:</span>
        <span className="task-count-badge task-count-badge--neutral">3</span>
        <span style={{ fontSize: T.type.caption, color: C.secondary, marginLeft: T.space.xs }}>Success:</span>
        <span className="task-count-badge task-count-badge--success">7</span>
        <span style={{ fontSize: T.type.caption, color: C.secondary, marginLeft: T.space.xs }}>Warning:</span>
        <span className="task-count-badge task-count-badge--warning">2</span>
        <span style={{ fontSize: T.type.caption, color: C.secondary, marginLeft: T.space.xs }}>Error:</span>
        <span className="task-count-badge task-count-badge--error">1</span>
      </div>
    </SubSection>
    <SubSection label="Avatar badges (with status indicator)">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="task-avatar-badge">
          <Avatar initials="AB" size={32} color="var(--ant-primary-color, #1890ff)" />
          <span className="task-avatar-badge__status task-avatar-badge__status--online" />
        </div>
        <span style={{ fontSize: T.type.caption, color: C.secondary }}>Online</span>
        <div className="task-avatar-badge">
          <Avatar initials="CD" size={32} color="#26a69a" />
          <span className="task-avatar-badge__status task-avatar-badge__status--offline" />
        </div>
        <span style={{ fontSize: T.type.caption, color: C.secondary }}>Offline</span>
        <div className="task-avatar-badge">
          <Avatar initials="EF" size={32} color="#f97316" />
          <span className="task-avatar-badge__status task-avatar-badge__status--busy" />
        </div>
        <span style={{ fontSize: T.type.caption, color: C.secondary }}>Busy</span>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 8. Forms & Inputs (Components)
// ---------------------------------------------------------------------------

const FormsPreview = () => (
  <Section
    id="sec-8"
    title="8. Forms & Inputs"
    description="Input fields, selects, and form layouts from _forms.scss. Shows default, hover, focus, disabled, and error states."
  >
    <div className="tm-form">
      <SubSection label="Text inputs">
        <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Default</div>
            <input
              className="tm-input"
              placeholder="Enter task name..."
              readOnly
            />
          </div>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>With value</div>
            <input
              className="tm-input"
              defaultValue="Homepage redesign"
              readOnly
            />
          </div>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Disabled</div>
            <input
              className="tm-input"
              defaultValue="Locked field"
              disabled
            />
          </div>
        </div>
      </SubSection>
      <SubSection label="Large input variant">
        <div style={{ maxWidth: 400 }}>
          <input
            className="tm-input tm-input--lg"
            placeholder="Large input for headings..."
            readOnly
          />
        </div>
      </SubSection>
      <SubSection label="Textarea">
        <div style={{ maxWidth: 500 }}>
          <textarea
            className="tm-input"
            style={{ height: 80, lineHeight: '1.5', padding: T.space.sm, resize: 'vertical' }}
            placeholder="Enter description..."
            readOnly
          />
        </div>
      </SubSection>
      <SubSection label="Form field with label">
        <div style={{ maxWidth: 300 }}>
          <div style={{ paddingBottom: T.space.xs }}>
            <label style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.body }}>
              <span style={{ color: T.color.forbidden, marginRight: T.space.xxs }}>*</span>
              Task Name
            </label>
          </div>
          <input
            className="tm-input"
            placeholder="Required field"
            readOnly
          />
          <div style={{ fontSize: T.type.caption, marginTop: T.space.xxs, color: C.secondary }}>
            Give your task a descriptive name
          </div>
        </div>
      </SubSection>
      <SubSection label="Error state">
        <div style={{ maxWidth: 300 }}>
          <div style={{ paddingBottom: T.space.xs }}>
            <label style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.body }}>
              <span style={{ color: T.color.forbidden, marginRight: T.space.xxs }}>*</span>
              Task Name
            </label>
          </div>
          <input
            className="tm-input"
            defaultValue=""
            style={{ borderColor: T.color.forbidden }}
            readOnly
          />
          <div style={{ fontSize: T.type.caption, marginTop: T.space.xxs, color: T.color.forbidden }}>
            Task name is required
          </div>
        </div>
      </SubSection>
      <SubSection label="Custom select (styled dropdown)">
        <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Closed</div>
            <div className="tm-custom-select">
              <div className="tm-custom-select__trigger">
                <span className="tm-custom-select__value">High</span>
                <span className="tm-custom-select__chevron">&#9662;</span>
              </div>
            </div>
          </div>
          <div style={{ flex: '1 1 240px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Open</div>
            <div className="tm-custom-select tm-custom-select--open">
              <div className="tm-custom-select__trigger">
                <span className="tm-custom-select__value">High</span>
                <span className="tm-custom-select__chevron">&#9652;</span>
              </div>
              <div className="tm-custom-select__dropdown">
                {[
                  { value: 'low', label: 'Low', color: '#22c55e' },
                  { value: 'medium', label: 'Medium', color: '#eab308' },
                  { value: 'high', label: 'High', color: '#f97316', active: true },
                  { value: 'critical', label: 'Critical', color: '#dc2626' },
                ].map((opt) => (
                  <div
                    key={opt.value}
                    className={`tm-custom-select__option${opt.active ? ' tm-custom-select__option--active' : ''}`}
                  >
                    <span className="tm-custom-select__option-dot" style={{ background: opt.color }} />
                    {opt.label}
                    {opt.active && <span className="tm-custom-select__check">&#10003;</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ flex: '1 1 240px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Placeholder</div>
            <div className="tm-custom-select">
              <div className="tm-custom-select__trigger">
                <span className="tm-custom-select__placeholder">Select urgency&hellip;</span>
                <span className="tm-custom-select__chevron">&#9662;</span>
              </div>
            </div>
          </div>
        </div>
      </SubSection>
      <SubSection label="Custom select with label + helper">
        <div style={{ maxWidth: 300 }}>
          <div style={{ paddingBottom: T.space.xs }}>
            <label style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.body }}>
              <span style={{ color: T.color.forbidden, marginRight: T.space.xxs }}>*</span>
              Urgency
            </label>
          </div>
          <div className="tm-custom-select">
            <div className="tm-custom-select__trigger">
              <span className="tm-custom-select__placeholder">Choose urgency level&hellip;</span>
              <span className="tm-custom-select__chevron">&#9662;</span>
            </div>
          </div>
          <div style={{ fontSize: T.type.caption, marginTop: T.space.xxs, color: C.secondary }}>
            Set the priority level for this task
          </div>
        </div>
      </SubSection>
      <SubSection label="Native selects (browser-styled dropdown)">
        <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Default</div>
            <select className="tm-select" defaultValue="">
              <option value="" disabled>Select urgency...</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>With value</div>
            <select className="tm-select" defaultValue="high">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div style={{ flex: '1 1 200px', maxWidth: 280 }}>
            <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.label, marginBottom: T.space.xs }}>Disabled</div>
            <select className="tm-select" defaultValue="medium" disabled>
              <option value="medium">Medium</option>
            </select>
          </div>
        </div>
      </SubSection>
    </div>
  </Section>
);

// ---------------------------------------------------------------------------
// 12. Modals (Components)
// ---------------------------------------------------------------------------

const ModalPreview = () => (
  <Section
    id="sec-12"
    title="12. Modals"
    description="Modal dialog layouts from _modals.scss. Glassmorphism frosted glass effect with structured header/body/footer."
  >
    <SubSection label="Standard modal (embedded preview)">
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div
          className="task-modal"
          style={{
            background: 'var(--preview-glass-medium)',
            backdropFilter: 'blur(24px) saturate(160%)',
            WebkitBackdropFilter: 'blur(24px) saturate(160%)',
            border: '1px solid var(--preview-glass-subtle)',
            borderRadius: T.radius.xxl,
            overflow: 'hidden',
            boxShadow: 'var(--preview-shadow-modal)',
          }}
        >
          {/* Header */}
          <div style={{
            padding: `${T.space.md}px ${T.space.lg}px`,
            borderBottom: '1px solid var(--preview-border-light)',
            background: 'var(--preview-glass-header)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: T.type.bodyLg, fontWeight: T.weight.semibold, color: C.text, letterSpacing: '-0.01em' }}>Delete Task</div>
            <div style={{
              width: 32, height: 32, borderRadius: T.radius.md,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.muted, cursor: 'pointer', fontSize: T.type.bodyLg,
              transition: 'color 180ms ease',
            }}>
              &#x2715;
            </div>
          </div>
          {/* Body */}
          <div style={{ padding: T.space.lg }}>
            <p style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7, margin: 0 }}>
              Are you sure you want to delete <strong>&quot;Homepage Redesign&quot;</strong>?
              This action cannot be undone.
            </p>
            <div style={{
              marginTop: T.space.md, padding: `${T.space.sm}px ${T.space.md}px`,
              background: 'rgba(202,138,4,0.06)',
              borderRadius: T.radius.input, fontSize: T.type.caption, color: T.color.warningDark,
              border: '1px solid rgba(202,138,4,0.12)',
              lineHeight: 1.5,
            }}>
              This task has 3 comments and 2 attachments that will also be removed.
            </div>
          </div>
          {/* Footer */}
          <div style={{
            padding: `${T.space.md}px ${T.space.lg}px`,
            borderTop: '1px solid var(--preview-border-light)',
            background: 'var(--preview-glass-subtle)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: T.space.sm,
          }}>
            <button type="button" className="tm-btn tm-btn--secondary">
              Cancel
            </button>
            <button type="button" className="tm-btn tm-btn--danger">
              Delete
            </button>
          </div>
        </div>
      </div>
    </SubSection>
    <SubSection label="Modal sizes">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="task-tag" style={{ background: C.bgAlt, color: C.body }}>Small (400px)</span>
        <span className="task-tag" style={{ background: C.bgAlt, color: C.body }}>Medium (560px)</span>
        <span className="task-tag" style={{ background: C.bgAlt, color: C.body }}>Large (720px)</span>
        <span className="task-tag" style={{ background: primaryRgba(0.08), color: C.primary, border: `1px solid ${primaryRgba(0.2)}` }}>Wide (960px)</span>
      </div>
      <p style={{ fontSize: T.type.caption, color: C.secondary, margin: `${T.space.xs}px 0 0`, lineHeight: 1.5 }}>
        <strong style={{ color: C.text }}>Small–Large:</strong> Action modals — confirmations, forms, multi-step dialogs.<br />
        <strong style={{ color: C.text }}>Wide:</strong> Information modals — help centers, documentation, previews, read-heavy content with sidebar layouts.
      </p>
    </SubSection>
    <SubSection label="Mobile behavior (governance)">
      <p style={{ fontSize: T.type.caption, color: C.secondary, margin: 0, lineHeight: 1.7 }}>
        <strong style={{ color: C.text }}>All modals</strong> must use <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>wrapClassName="tags-modal"</code> to inherit shared mobile behavior.<br />
        <strong style={{ color: C.text }}>Below md (768px):</strong> Full-screen — border-radius: 0, min-height: 100vh, sticky header/footer, 44px touch targets on buttons.<br />
        <strong style={{ color: C.text }}>Width prop:</strong> Use <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>modal.sm / md / lg / wide</code> from designTokens.js — never percentages or hardcoded px.<br />
        <strong style={{ color: C.text }}>CSS handles the breakpoint.</strong> Do not toggle width in JS (e.g. <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>isMobile ? '100%' : 960</code>). The responsive block in _modals.scss overrides to full-screen automatically.<br />
        <strong style={{ color: C.text }}>Vertical position:</strong> All modals must use <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>centered</code> prop for consistent vertical centering.
      </p>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 19. Description & Comments (Patterns)
// ---------------------------------------------------------------------------

const CommentsPreview = () => (
  <Section
    id="sec-19"
    title="19. Description & Comments"
    description="Description card and comment thread patterns from _comments.scss. Asana-style collapsible description with blue accent."
  >
    <SubSection label="Description card (collapsed)">
      <div className="task-description-card">
        <div className="task-description-card__header">
          <span className="task-description-card__title">Description</span>
          <button type="button" className="task-description-card__toggle">
            Show more &#9662;
          </button>
        </div>
        <div className="task-description-card__content">
          <div className="task-description-card__text">
            Redesign the homepage to improve conversion rates and user engagement.
            Focus on above-the-fold content, clear CTAs, and mobile responsiveness.
            Reference the brand guidelines doc for typography and color usage.
            <br /><br />
            Key deliverables:
            <br />- Wireframes for desktop and mobile
            <br />- High-fidelity mockups
            <br />- Interactive prototype
            <br />- Developer handoff specs
          </div>
        </div>
      </div>
    </SubSection>
    <SubSection label="Description card (expanded)">
      <div className="task-description-card">
        <div className="task-description-card__header">
          <span className="task-description-card__title">Description</span>
          <button type="button" className="task-description-card__toggle">
            Show less &#9652;
          </button>
        </div>
        <div className="task-description-card__content task-description-card__content--expanded">
          <div className="task-description-card__text">
            Redesign the homepage to improve conversion rates and user engagement.
            Focus on above-the-fold content, clear CTAs, and mobile responsiveness.
          </div>
        </div>
      </div>
    </SubSection>
    <SubSection label="Inline description variant">
      <div className="task-description-inline">
        <div className="task-description-inline__label">
          <span style={{ fontSize: T.type.caption, color: C.body, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Description
          </span>
        </div>
        <div style={{ padding: T.space.md }}>
          <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.6 }}>
            This is the inline description variant that scrolls with the comment thread.
          </div>
        </div>
      </div>
    </SubSection>
    <SubSection label="Comment thread (simplified)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.sm, maxWidth: 520 }}>
        {/* Comment item */}
        <div style={{
          display: 'flex', gap: T.space.sm, padding: `${T.space.md}px ${T.space.md}px`,
          borderRadius: T.radius.lg, background: 'var(--preview-glass-subtle)',
          border: '1px solid ' + C.border, transition: 'all 180ms ease',
        }}>
          <Avatar initials="JD" size={32} color="var(--ant-primary-color, #1890ff)" />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: T.space.xs, marginBottom: T.space.xxs }}>
              <span style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.strong }}>John Doe</span>
              <span style={{ fontSize: T.type.caption, color: C.muted }}>2 hours ago</span>
            </div>
            <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.65 }}>
              Updated the wireframes based on client feedback. Ready for review.
            </div>
          </div>
        </div>
        {/* Comment item 2 */}
        <div style={{
          display: 'flex', gap: T.space.sm, padding: `${T.space.md}px ${T.space.md}px`,
          borderRadius: T.radius.lg, background: 'var(--preview-glass-subtle)',
          border: '1px solid ' + C.border, transition: 'all 180ms ease',
        }}>
          <Avatar initials="SM" size={32} color="#26a69a" />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: T.space.xs, marginBottom: T.space.xxs }}>
              <span style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.strong }}>Sarah Miller</span>
              <span style={{ fontSize: T.type.caption, color: C.muted }}>1 hour ago</span>
            </div>
            <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.65 }}>
              Looks great! Just a few minor spacing adjustments needed on the mobile version.
            </div>
          </div>
        </div>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 13. Pagination (Components)
// ---------------------------------------------------------------------------

// Governed pagination sizes — aligned with PaginationTable.computePageSizeOptions()
const PAGE_SIZES = [10, 20, 50, 100, 200];

const PaginationPreview = () => (
  <Section
    id="sec-13"
    title="13. Pagination"
    description="Three pagination variants: full bar (tables), compact bar (drawers/modals — dropdown page size, no summary), and standalone nav."
  >
    <SubSection label="Full pagination bar">
      <div className="tm-pagination-bar">
        <div className="tm-pagination-bar__summary">
          Showing <strong>1 – 10</strong> of <strong>19</strong> projects
        </div>
        <div className="tm-pagination-bar__nav">
          <ul className="tm-pagination">
            <li className="tm-pagination__prev">
              <button type="button">&lt;</button>
            </li>
            {[1, 2].map((n) => (
              <li
                key={n}
                className={`tm-pagination__item${n === 1 ? ' tm-pagination__item--active' : ''}`}
              >
                <a href="#" onClick={(e) => e.preventDefault()}>{n}</a>
              </li>
            ))}
            <li className="tm-pagination__next">
              <button type="button">&gt;</button>
            </li>
          </ul>
        </div>
        <div className="tm-pagination-bar__page-size">
          <span className="tm-pagination-bar__page-size-label">Show:</span>
          {PAGE_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={`tm-pagination-bar__page-size-btn${size === 10 ? ' tm-pagination-bar__page-size-btn--active' : ''}`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
    </SubSection>
    <SubSection label="Compact pagination bar (drawers, sidebars, modals)">
      <p style={{ fontSize: T.type.caption, color: C.secondary, margin: `0 0 ${T.space.sm}px 0`, lineHeight: 1.5 }}>
        3-column CSS Grid: <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>1fr auto 1fr</code> — spacer left, nav centered, dropdown right.
      </p>
      <div className="tm-pagination-bar tm-pagination-bar--compact">
        {/* Grid slot 1: empty (::before pseudo-element acts as spacer) */}
        {/* Grid slot 2: nav — truly centered */}
        <div className="tm-pagination-bar__nav">
          <ul className="tm-pagination">
            <li className="tm-pagination__prev">
              <button type="button">&lt;</button>
            </li>
            {[1, 2, 3].map((n) => (
              <li
                key={n}
                className={`tm-pagination__item${n === 1 ? ' tm-pagination__item--active' : ''}`}
              >
                <a href="#" onClick={(e) => e.preventDefault()}>{n}</a>
              </li>
            ))}
            <li className="tm-pagination__next">
              <button type="button">&gt;</button>
            </li>
          </ul>
        </div>
        {/* Grid slot 3: page-size dropdown — pinned right */}
        <div className="tm-pagination-bar__page-size-dropdown">
          <select defaultValue={10} className="tm-pagination-bar__size-select">
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        </div>
      </div>
    </SubSection>
    <SubSection label="Standalone pagination (nav only)">
      <div className="task-pagination">
        <ul className="tm-pagination">
          <li className="tm-pagination__prev">
            <button type="button">&lt;</button>
          </li>
          {[1, 2, 3, 4, 5].map((n) => (
            <li
              key={n}
              className={`tm-pagination__item${n === 2 ? ' tm-pagination__item--active' : ''}`}
            >
              <a href="#" onClick={(e) => e.preventDefault()}>{n}</a>
            </li>
          ))}
          <li className="tm-pagination__next">
            <button type="button">&gt;</button>
          </li>
        </ul>
      </div>
    </SubSection>
    <SubSection label="Placement notes">
      <p style={{ fontSize: T.type.caption, color: C.secondary, margin: 0, lineHeight: 1.5 }}>
        <strong style={{ color: C.text }}>Full bar:</strong> Below main tables — summary left, nav center, page-size buttons right.<br />
        <strong style={{ color: C.text }}>Compact bar:</strong> Drawers, sidebars, modals — no summary text, page-size as dropdown to save horizontal space. Use <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>compact</code> prop on TagsPagination.<br />
        <strong style={{ color: C.text }}>Nav only:</strong> Minimal contexts where page size is fixed.<br />
        On mobile, page items expand to 44px touch targets and the page size selector stacks.
        Active page uses <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>$task-primary</code> background.
      </p>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// Mobile content (rendered inside Responsive & Mobile section)
// ---------------------------------------------------------------------------

// Mobile content — renders inside Responsive & Mobile section (no standalone Section wrapper)
const MobileContent = () => (
  <>
    <div className="task-design-preview__row">
      <div className="task-design-preview__col">
        <SubSection label="Floating summary pill">
          <div className="task-design-preview__viewport task-design-preview__viewport--mobile">
            <div className="task-design-preview__viewport-label">375px</div>
            <div style={{ padding: T.space.md, background: C.bg, minHeight: 200, position: 'relative' }}>
              <div style={{ fontSize: T.type.caption, color: C.secondary, marginBottom: T.space.xs, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: T.weight.semibold }}>
                Task list content above...
              </div>
              {/* Summary pill (positioned relative for demo) */}
              <div style={{
                position: 'absolute', bottom: 16, left: 16, right: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: T.space.sm, padding: `${T.space.sm}px ${T.space.md}px`,
                background: 'var(--preview-glass-medium)',
                border: '1px solid var(--preview-border)',
                borderRadius: T.radius.xl,
                boxShadow: 'var(--preview-shadow-pill)',
                cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: T.space.sm, flex: 1, minWidth: 0 }}>
                  <div style={{ width: 3, height: 24, borderRadius: 2, background: T.color.urgencyHigh, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      Homepage Redesign
                    </div>
                    <div style={{ fontSize: T.type.caption, color: C.secondary }}>Due Jan 15</div>
                  </div>
                </div>
                <Avatar initials="JD" size={24} color="var(--ant-primary-color, #1890ff)" />
                <span style={{ fontSize: T.type.small, color: C.faint }}>&#9650;</span>
              </div>
            </div>
          </div>
        </SubSection>
      </div>
      <div className="task-design-preview__col">
        <SubSection label="Bottom sheet (open state)">
          <div className="task-design-preview__viewport task-design-preview__viewport--mobile">
            <div className="task-design-preview__viewport-label">375px</div>
            <div style={{ background: 'var(--preview-overlay-bg)', minHeight: 300, position: 'relative' }}>
              {/* Sheet */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'var(--preview-glass-strong)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: `${T.radius.xxl}px ${T.radius.xxl}px 0 0`,
                boxShadow: 'var(--preview-shadow-sheet)',
                padding: `${T.space.xs}px 0 0`,
              }}>
                {/* Handle */}
                <div style={{
                  width: 36, height: 4, borderRadius: 2,
                  background: C.faint, margin: `0 auto ${T.space.sm}px`,
                }} />
                {/* Sheet content */}
                <div style={{ padding: `0 ${T.space.lg}px ${T.space.lg}px` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: T.space.xs, marginBottom: T.space.sm }}>
                    <div style={{ width: 3, height: 20, borderRadius: 2, background: T.color.urgencyHigh }} />
                    <div style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text }}>Homepage Redesign</div>
                    <span className="task-priority task-priority--high" style={{ marginLeft: 'auto' }}>High</span>
                  </div>
                  {/* Stats grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.space.xs, marginBottom: T.space.sm }}>
                    {[
                      { label: 'Status', value: 'In Progress', color: '#3b82f6' },
                      { label: 'Due Date', value: 'Jan 15, 2026', color: C.body },
                      { label: 'Assignee', value: 'John Doe', color: C.body },
                      { label: 'Project', value: 'Marketing', color: C.body },
                    ].map((stat) => (
                      <div key={stat.label} style={{ padding: `${T.space.xs}px ${T.space.sm}px`, background: 'var(--preview-glass-subtle)', borderRadius: T.radius.input, border: '1px solid var(--preview-border-light)' }}>
                        <div style={{ fontSize: T.type.caption, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: T.weight.semibold, marginBottom: T.space.xxs }}>{stat.label}</div>
                        <div style={{ fontSize: T.type.caption, fontWeight: T.weight.medium, color: stat.color }}>{stat.value}</div>
                      </div>
                    ))}
                  </div>
                  <button type="button" style={{
                    width: '100%', height: T.size.btnLg, borderRadius: T.radius.lg, border: 'none',
                    background: `linear-gradient(180deg, ${primaryRgba(0.85)} 0%, var(--ant-primary-color, #1890ff) 100%)`,
                    color: T.color.onDark, fontSize: T.type.body, fontWeight: T.weight.semibold, cursor: 'pointer',
                    boxShadow: `0 2px 12px ${primaryRgba(0.3)}`,
                    transition: 'all 180ms ease',
                  }}>
                    View Full Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </SubSection>
      </div>
    </div>
    <SubSection label="Animation tokens">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
        {[
          { label: 'Spring bounce', value: 'cubic-bezier(0.32, 0.72, 0, 1)' },
          { label: 'Sheet duration', value: '450ms' },
          { label: 'Fast duration', value: '200ms' },
          { label: 'Sheet blur', value: '16px' },
        ].map((t) => (
          <div key={t.label} style={{
            padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
            borderRadius: T.radius.input, fontSize: T.type.caption, border: '1px solid ' + C.border,
            transition: 'all 180ms ease',
          }}>
            <div style={{ color: C.muted, fontSize: T.type.caption, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: T.weight.semibold, marginBottom: T.space.xxs }}>{t.label}</div>
            <code style={{ color: C.body, fontSize: T.type.caption, fontFamily: C.mono }}>{t.value}</code>
          </div>
        ))}
      </div>
    </SubSection>
  </>
);

// ---------------------------------------------------------------------------
// 23. Accessibility (Governance)
// ---------------------------------------------------------------------------

const AccessibilityPreview = () => (
  <Section
    id="sec-23"
    title="23. Accessibility"
    description="Focus rings, screen reader utilities, skip links, and media queries from _accessibility.scss. WCAG 2.1 AA compliant."
  >
    <SubSection label="Focus ring styles">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          style={{
            padding: `${T.space.xs}px ${T.space.lg}px`, borderRadius: T.radius.lg, border: '1px solid var(--preview-border-med)',
            background: 'var(--preview-card-bg)', cursor: 'pointer', fontSize: T.type.small,
            outline: `3px solid ${T.color.focus}`, outlineOffset: 2,
          }}
        >
          Focus visible
        </button>
        <input
          readOnly
          defaultValue="Focused input"
          style={{
            height: 40, padding: `0 ${T.space.sm}px`, borderRadius: T.radius.input, background: 'var(--preview-card-bg)', color: C.text,
            border: '1px solid var(--ant-primary-color, #1890ff)', fontSize: T.type.small,
            outline: `3px solid ${T.color.focus}`, outlineOffset: 2,
            boxShadow: `0 0 0 3px ${primaryRgba(0.15)}`,
          }}
        />
        <div style={{
          width: 32, height: 32, borderRadius: T.radius.md, border: '1px solid var(--preview-border-med)',
          background: 'var(--preview-card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: T.type.bodyLg, cursor: 'pointer',
          outline: `3px solid ${T.color.focus}`, outlineOffset: 2,
        }}>
          &#x2715;
        </div>
      </div>
      <div style={{ marginTop: T.space.xs, fontSize: T.type.caption, color: C.secondary }}>
        Focus ring: 3px solid #0066cc (meets WCAG 3:1 contrast), 2px offset
      </div>
    </SubSection>
    <SubSection label="Skip link (keyboard only)">
      <div style={{
        display: 'inline-block', padding: `${T.space.xs}px ${T.space.md}px`, borderRadius: `0 0 ${T.radius.md}px ${T.radius.md}px`,
        background: `linear-gradient(180deg, ${primaryRgba(0.85)} 0%, var(--ant-primary-color, #1890ff) 100%)`, color: T.color.onDark,
        fontWeight: T.weight.semibold, fontSize: T.type.small, boxShadow: `0 2px 8px ${primaryRgba(0.25)}`,
      }}>
        Skip to main content
      </div>
      <div style={{ marginTop: T.space.xs, fontSize: T.type.caption, color: C.secondary }}>
        Hidden by default, appears on Tab focus. Uses <code style={{ fontSize: T.type.caption, padding: '2px 6px', background: primaryRgba(0.06), borderRadius: T.radius.xs, fontFamily: C.mono, color: C.label }}>$task-primary</code> background.
      </div>
    </SubSection>
    <SubSection label="Screen reader utilities">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xs }}>
        {[
          { cls: '.sr-only', desc: 'Visually hidden, accessible to screen readers' },
          { cls: '.sr-only-focusable', desc: 'Hidden until focused (keyboard navigation)' },
          { cls: '[aria-busy="true"]', desc: 'Loading states with cursor: wait + reduced opacity' },
          { cls: '[aria-invalid="true"]', desc: 'Error states with red border' },
          { cls: '[role="alert"]', desc: 'Live region announcements (subtle left border)' },
        ].map((item) => (
          <div key={item.cls} style={{ display: 'flex', gap: T.space.sm, alignItems: 'baseline', padding: `${T.space.xs}px 0` }}>
            <code style={{ fontSize: T.type.caption, padding: '3px 8px', background: primaryRgba(0.06), borderRadius: T.radius.sm, whiteSpace: 'nowrap', color: C.label, fontFamily: C.mono, fontWeight: T.weight.medium }}>
              {item.cls}
            </code>
            <span style={{ fontSize: T.type.caption, color: C.secondary }}>{item.desc}</span>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Media query support">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap' }}>
        {[
          { label: 'prefers-reduced-motion', desc: 'Disables animations, transitions set to 0.01ms' },
          { label: 'prefers-contrast: high', desc: 'Stronger borders, bolder focus indicators' },
          { label: 'prefers-color-scheme', desc: 'Not currently implemented (future)' },
        ].map((mq) => (
          <div key={mq.label} style={{
            padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
            borderRadius: T.radius.input, flex: '1 1 200px',
            border: '1px solid ' + C.border,
            transition: 'all 180ms ease',
          }}>
            <code style={{ fontSize: T.type.caption, color: C.body, fontFamily: C.mono, fontWeight: T.weight.medium }}>@media ({mq.label})</code>
            <div style={{ fontSize: T.type.caption, color: C.secondary, marginTop: T.space.xxs }}>{mq.desc}</div>
          </div>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 22. Responsive & Mobile (Patterns)
// ---------------------------------------------------------------------------

const ResponsivePreview = () => (
  <Section
    id="sec-22"
    title="22. Responsive & Mobile"
    description="Simulated viewports showing mobile and tablet adaptations, plus mobile-specific interaction patterns."
  >
    <div className="task-design-preview__row">
      <div className="task-design-preview__col">
        <SubSection label="Mobile (375px)">
          <div className="task-design-preview__viewport task-design-preview__viewport--mobile">
            <div className="task-design-preview__viewport-label">375px</div>
            <div style={{ padding: T.space.sm, background: C.bg }}>
              {/* Mobile card layout */}
              {TABLE_ROWS.slice(0, 3).map((row) => {
                const uColor = URGENCY_COLORS[row.urgency]?.hex || T.color.neutral;
                const statusInfo = STATUS_LIST.find((s) => s.key === row.status);
                return (
                  <div
                    key={row.id}
                    style={{
                      background: 'var(--preview-card-bg)',
                      borderRadius: T.radius.lg,
                      padding: `${T.space.sm}px ${T.space.md}px`,
                      marginBottom: T.space.xs,
                      borderLeft: `3px solid ${uColor}`,
                      boxShadow: 'var(--preview-shadow-card)',
                      border: '1px solid ' + C.border,
                      borderLeftWidth: 3,
                      borderLeftColor: uColor,
                      transition: 'all 180ms ease',
                    }}
                  >
                    <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xs, letterSpacing: '-0.01em' }}>{row.name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, textTransform: 'uppercase', letterSpacing: '0.04em', color: statusInfo?.color, background: `${statusInfo?.color}12`, padding: '2px 8px', borderRadius: T.radius.sm }}>
                        {statusInfo?.label}
                      </span>
                      <Avatar initials={row.assignee} size={20} />
                    </div>
                  </div>
                );
              })}
              {/* Mobile FAB */}
              <div style={{
                position: 'relative', display: 'flex', justifyContent: 'flex-end', marginTop: T.space.sm,
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: T.radius.xl,
                  background: `linear-gradient(135deg, ${primaryRgba(0.85)} 0%, var(--ant-primary-color, #1890ff) 50%, ${primaryRgba(0.9)} 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: T.color.onDark, fontSize: T.type.h2, fontWeight: T.weight.regular,
                  boxShadow: `0 4px 16px ${primaryRgba(0.35)}, 0 8px 32px ${primaryRgba(0.15)}`,
                  transition: 'transform 180ms ease, box-shadow 180ms ease',
                }}>
                  +
                </div>
              </div>
            </div>
          </div>
        </SubSection>
      </div>
      <div className="task-design-preview__col">
        <SubSection label="Tablet (768px)">
          <div className="task-design-preview__viewport task-design-preview__viewport--tablet">
            <div className="task-design-preview__viewport-label">768px</div>
            <div style={{ padding: T.space.md, background: C.bg }}>
              {/* Tablet: kanban with fixed columns */}
              <div style={{ display: 'flex', gap: T.space.md, overflowX: 'auto' }}>
                {['In Progress', 'Review'].map((col) => (
                  <div key={col} style={{ flex: '0 0 320px', background: C.bgAlt, borderRadius: T.radius.lg, padding: T.space.md, border: '1px solid ' + C.border }}>
                    <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, marginBottom: T.space.sm, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {col}
                    </div>
                    <div style={{
                      background: 'var(--preview-card-bg)', borderRadius: T.radius.lg, padding: `${T.space.sm}px ${T.space.md}px`, marginBottom: T.space.xs,
                      boxShadow: 'var(--preview-shadow-card)',
                      borderLeft: `3px solid ${T.color.urgencyHigh}`,
                      border: '1px solid ' + C.border,
                      borderLeftWidth: 3,
                      borderLeftColor: T.color.urgencyHigh,
                      transition: 'all 180ms ease',
                    }}>
                      <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, letterSpacing: '-0.01em' }}>Sample task card</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SubSection>
      </div>
    </div>

    <SubSection label="Pattern: Table-to-Card transformation">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.sm }}>
        <div style={{
          padding: `${T.space.md}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
          borderRadius: T.radius.lg, border: '1px solid ' + C.border,
        }}>
          <div style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xs }}>Reusable responsive pattern</div>
          <div style={{ fontSize: T.type.caption, color: C.body, lineHeight: 1.7 }}>
            When a data table has more than 4 columns, convert it to expandable cards on screens &le;768px.
            Primary columns (ID, name, status, urgency) show by default. Secondary columns (assignee, date)
            collapse behind a &ldquo;More info&rdquo; toggle with slide-down animation.
          </div>
        </div>
        <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap' }}>
          {[
            { label: 'Breakpoint', value: '768px (tablet)' },
            { label: 'Toggle', value: 'Segmented control' },
            { label: 'Auto-switch', value: 'Cards on mobile' },
            { label: 'Animation', value: 'preview-fade-in 200ms' },
          ].map((item) => (
            <div key={item.label} style={{
              padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
              borderRadius: T.radius.md, border: '1px solid ' + C.border, flex: '1 1 140px',
            }}>
              <div style={{ fontSize: T.type.caption, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: T.weight.semibold, marginBottom: T.space.xxs }}>{item.label}</div>
              <code style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.body }}>{item.value}</code>
            </div>
          ))}
        </div>
      </div>
    </SubSection>

    {/* Mobile interaction patterns (merged from Mobile Bottom Sheet) */}
    <MobileContent />
  </Section>
);

// ---------------------------------------------------------------------------
// App Header & Footer (content-only — merged into Header & Navigation)
// ---------------------------------------------------------------------------

const HeaderFooterContent = () => (
  <>
    <SubSection label="App header (global)">
      <div className="tm-header">
        <div className="tm-header__left">
          <div className="tm-header__logo">MJ</div>
          <nav className="tm-header__breadcrumb">
            <span className="tm-header__breadcrumb-item">Dashboard</span>
            <span className="tm-header__breadcrumb-sep">/</span>
            <span className="tm-header__breadcrumb-item">Campaigns</span>
            <span className="tm-header__breadcrumb-sep">/</span>
            <span className="tm-header__breadcrumb-item tm-header__breadcrumb-item--current">Spring 2026</span>
          </nav>
        </div>
        <div className="tm-header__center">
          <div className="tm-header__search">
            <span className="tm-header__search-icon"><SearchOutlined /></span>
            <span className="tm-header__search-text">Search anything&hellip;</span>
            <kbd className="tm-header__search-kbd">⌘K</kbd>
          </div>
        </div>
        <div className="tm-header__right">
          <div className="tm-header__actions">
            <button type="button" className="tm-header__action-btn tm-header__action-btn--create" aria-label="Quick create"><PlusOutlined /></button>
            <button type="button" className="tm-header__action-btn" aria-label="Notifications">
              <BellOutlined />
              <span className="tm-header__notification-dot" />
            </button>
            <button type="button" className="tm-header__action-btn" aria-label="Toggle theme"><MoonOutlined /></button>
            <div className="tm-header__avatar">
              <span>RG</span>
              <span className="tm-header__avatar-caret">▾</span>
            </div>
          </div>
        </div>
      </div>
    </SubSection>

    <SubSection label="Contextual footer">
      <div className="tm-footer">
        <div className="tm-footer__copyright">&copy; 2026 MediaJel &middot; v2.4.1</div>
        <div className="tm-footer__status">
          <span className="tm-footer__status-dot" />
          All systems operational
        </div>
        <div className="tm-footer__links">
          <kbd className="tm-footer__kbd">⌘K</kbd>
          <span className="tm-footer__link">Help</span>
          <span className="tm-footer__link">Docs</span>
        </div>
      </div>
    </SubSection>
  </>
);

// ---------------------------------------------------------------------------
// 20. Sidebar (Patterns)
// ---------------------------------------------------------------------------

const SIDEBAR_NAV = [
  { group: 'MAIN', items: [
    { icon: <AreaChartOutlined />, label: 'Campaigns', active: true, badge: '4' },
    { icon: <DashboardOutlined />, label: 'Overview' },
    { icon: <AreaChartOutlined />, label: 'Reports' },
    { icon: <DollarCircleOutlined />, label: 'Paid Search' },
    { icon: <SearchOutlined />, label: 'SEO Domains' },
    { icon: <BookOutlined />, label: 'Library' },
    { icon: <DotChartOutlined />, label: 'Demographic' },
  ]},
  { group: 'ATTENTION SIGNALS', items: [
    { icon: <StarOutlined />, label: 'Advertiser', hover: true },
    { icon: <ReadOutlined />, label: 'Publisher' },
    { icon: <FileTextOutlined />, label: 'Invoices' },
  ]},
  { group: 'LIBRARY', items: [
    { icon: <UsergroupAddOutlined />, label: 'Audience', badge: '12' },
    { icon: <AppstoreAddOutlined />, label: 'Creatives' },
    { icon: <CompressOutlined />, label: 'Targeting' },
    { icon: <TagOutlined />, label: 'Tags' },
    { icon: <EnvironmentOutlined />, label: 'Retail Stores' },
  ]},
  { group: 'TASK MANAGEMENT', items: [
    { icon: <UnorderedListOutlined />, label: 'Tasks', badge: '8' },
    { icon: <FileAddOutlined />, label: 'Create Task' },
  ]},
];

const SidebarNavItem = ({ icon, label, badge, alert, active, hover, collapsed }) => {
  const cls = [
    'tm-sidebar__nav-item',
    active && 'tm-sidebar__nav-item--active',
    hover && 'tm-sidebar__nav-item--hover',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <span className="tm-sidebar__nav-icon">{icon}</span>
      {!collapsed && (
        <>
          <span className="tm-sidebar__nav-label">{label}</span>
          {badge && (
            <span className={`tm-sidebar__nav-badge${alert ? ' tm-sidebar__nav-badge--alert' : ''}`}>
              {badge}
            </span>
          )}
        </>
      )}
      {collapsed && active && <span className="tm-sidebar__active-dot" />}
    </div>
  );
};

const SidebarPreview = () => (
  <Section
    id="sec-20"
    title="20. Sidebar"
    description="Full navigation sidebar specimen with expanded and collapsed states. Demonstrates org switching, search, grouped navigation, badges, and user profile area."
  >
    <div className="task-design-preview__row">
      <div className="task-design-preview__col" style={{ flex: '0 0 280px' }}>
        <SubSection label="Expanded (240px)">
          <div className="tm-sidebar">
            {/* Org switcher */}
            <div className="tm-sidebar__org-switcher">
              <div className="tm-sidebar__org-avatar">M</div>
              <div className="tm-sidebar__org-info">
                <span className="tm-sidebar__org-name">Marketing Team</span>
                <span className="tm-sidebar__org-chevron">▾</span>
              </div>
            </div>

            {/* Search */}
            <div className="tm-sidebar__search">
              <span className="tm-sidebar__search-icon"><SearchOutlined /></span>
              <span className="tm-sidebar__search-text">Search or jump to&hellip;</span>
              <kbd className="tm-sidebar__search-kbd">⌘K</kbd>
            </div>

            {/* Nav groups */}
            {SIDEBAR_NAV.map((section, gi) => (
              <div className="tm-sidebar__nav-group" key={gi}>
                {section.group && (
                  <div className="tm-sidebar__group-label">{section.group}</div>
                )}
                {section.items.map((item, ii) => (
                  <SidebarNavItem key={ii} {...item} />
                ))}
              </div>
            ))}

            {/* Divider */}
            <div className="tm-sidebar__divider" />

            {/* Admin section */}
            <div className="tm-sidebar__nav-group">
              <div className="tm-sidebar__group-label">ADMIN</div>
              <SidebarNavItem icon={<SettingOutlined />} label="Settings" />
              <SidebarNavItem icon={<TeamOutlined />} label="Organizations" />
              <SidebarNavItem icon={<ThunderboltOutlined />} label="System Alerts" />
              <SidebarNavItem icon={<InfoCircleOutlined />} label="Knowledge Base" />
            </div>

            {/* User area */}
            <div className="tm-sidebar__user-area">
              <div className="tm-sidebar__user-avatar">RG</div>
              <div className="tm-sidebar__user-info">
                <span className="tm-sidebar__user-name">Russell G.</span>
                <span className="tm-sidebar__user-role">Admin</span>
              </div>
              <span className="tm-sidebar__user-gear">⚙</span>
            </div>
          </div>
        </SubSection>
      </div>

      <div className="task-design-preview__col" style={{ flex: '0 0 100px' }}>
        <SubSection label="Collapsed (56px)">
          <div className="tm-sidebar tm-sidebar--collapsed">
            {/* Org icon */}
            <div className="tm-sidebar__org-switcher">
              <div className="tm-sidebar__org-avatar">M</div>
            </div>

            {/* Collapsed nav items */}
            {SIDEBAR_NAV.flatMap((section) => section.items).map((item, i) => (
              <SidebarNavItem key={i} {...item} collapsed />
            ))}

            <div className="tm-sidebar__divider" />

            <SidebarNavItem icon={<SettingOutlined />} label="Settings" collapsed />
            <SidebarNavItem icon={<TeamOutlined />} label="Organizations" collapsed />
            <SidebarNavItem icon={<ThunderboltOutlined />} label="System Alerts" collapsed />
            <SidebarNavItem icon={<InfoCircleOutlined />} label="Knowledge Base" collapsed />

            {/* Collapsed user */}
            <div className="tm-sidebar__user-area">
              <div className="tm-sidebar__user-avatar">RG</div>
            </div>
          </div>
        </SubSection>
      </div>
    </div>

    <SubSection label="Navigation item states">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.xs }}>
        {[
          { label: 'Default', cls: '' },
          { label: 'Hover', cls: 'tm-sidebar__nav-item--hover' },
          { label: 'Active', cls: 'tm-sidebar__nav-item--active' },
          { label: 'With badge', badge: '5' },
          { label: 'Alert badge', badge: '3', alert: true },
        ].map((state) => (
          <div key={state.label} style={{ flex: '0 0 200px' }}>
            <div style={{ fontSize: T.type.caption, color: C.muted, marginBottom: T.space.xxs, fontWeight: T.weight.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {state.label}
            </div>
            <div className={`tm-sidebar__nav-item ${state.cls || ''}`.trim()}>
              <span className="tm-sidebar__nav-icon"><AreaChartOutlined /></span>
              <span className="tm-sidebar__nav-label">Nav Item</span>
              {state.badge && (
                <span className={`tm-sidebar__nav-badge${state.alert ? ' tm-sidebar__nav-badge--alert' : ''}`}>
                  {state.badge}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 14. Icon Reference (Components)
// ---------------------------------------------------------------------------

const ICON_MAP = [
  { feature: 'CAMPAIGNS',       antIcon: 'AreaChartOutlined',      icon: <AreaChartOutlined /> },
  { feature: 'PAID_SEARCH',     antIcon: 'DollarCircleOutlined',   icon: <DollarCircleOutlined /> },
  { feature: 'SEO',             antIcon: 'SearchOutlined',         icon: <SearchOutlined /> },
  { feature: 'LIBRARY',         antIcon: 'BookOutlined',           icon: <BookOutlined /> },
  { feature: 'DEMOGRAPHIC',     antIcon: 'DotChartOutlined',       icon: <DotChartOutlined /> },
  { feature: 'ADVERTISER',      antIcon: 'StarOutlined',           icon: <StarOutlined /> },
  { feature: 'PUBLISHER',       antIcon: 'ReadOutlined',           icon: <ReadOutlined /> },
  { feature: 'INVOICES',        antIcon: 'FileTextOutlined',       icon: <FileTextOutlined /> },
  { feature: 'AUDIENCE',        antIcon: 'UsergroupAddOutlined',   icon: <UsergroupAddOutlined /> },
  { feature: 'CREATIVES',       antIcon: 'AppstoreAddOutlined',    icon: <AppstoreAddOutlined /> },
  { feature: 'TARGETING',       antIcon: 'CompressOutlined',       icon: <CompressOutlined /> },
  { feature: 'TAGS',            antIcon: 'TagOutlined',            icon: <TagOutlined /> },
  { feature: 'RETAILS_STORES',  antIcon: 'EnvironmentOutlined',    icon: <EnvironmentOutlined /> },
  { feature: 'TASKS',           antIcon: 'UnorderedListOutlined',  icon: <UnorderedListOutlined /> },
  { feature: 'CREATE_TASK',     antIcon: 'FileAddOutlined',        icon: <FileAddOutlined /> },
  { feature: 'SETTINGS',        antIcon: 'SettingOutlined',        icon: <SettingOutlined /> },
  { feature: 'ORGS',            antIcon: 'TeamOutlined',           icon: <TeamOutlined /> },
  { feature: 'KNOWLEDGE_BASE',  antIcon: 'InfoCircleOutlined',     icon: <InfoCircleOutlined /> },
  { feature: 'SYSTEM_ALERTS',   antIcon: 'ThunderboltOutlined',    icon: <ThunderboltOutlined /> },
  { feature: 'DASHBOARD',       antIcon: 'DashboardOutlined',      icon: <DashboardOutlined /> },
];

const IconReferencePreview = () => (
  <Section
    id="sec-14"
    title="14. Icon Reference"
    description="Complete mapping of @ant-design/icons Outlined components used across the application sidebar navigation. Icons are rendered live from the existing library — no additional dependencies."
  >
    <SubSection label="Icon library">
      <div style={{
        padding: `${T.space.md}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
        borderRadius: T.radius.lg, border: '1px solid var(--preview-border)', marginBottom: T.space.md,
      }}>
        <div style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xs }}>
          @ant-design/icons (Outlined)
        </div>
        <div style={{ fontSize: T.type.caption, color: C.body, lineHeight: 1.7 }}>
          Production sidebar uses <code style={{ fontFamily: C.mono, fontSize: T.type.caption, background: 'var(--preview-subtle-bg)', padding: '1px 5px', borderRadius: T.radius.xs }}>@ant-design/icons</code> Outlined
          variant via the <code style={{ fontFamily: C.mono, fontSize: T.type.caption, background: 'var(--preview-subtle-bg)', padding: '1px 5px', borderRadius: T.radius.xs }}>SidebarIcon</code> component
          in <code style={{ fontFamily: C.mono, fontSize: T.type.caption, background: 'var(--preview-subtle-bg)', padding: '1px 5px', borderRadius: T.radius.xs }}>src/admin/core/utils/general/index.js</code>.
          This preview imports and renders the same icons directly — no additional libraries needed.
        </div>
      </div>
    </SubSection>

    <SubSection label="Feature &rarr; icon mapping">
      <div className="tm-icon-ref">
        <div className="tm-icon-ref__header">
          <span className="tm-icon-ref__col tm-icon-ref__col--feature">Feature</span>
          <span className="tm-icon-ref__col tm-icon-ref__col--ant">Ant Design Icon</span>
          <span className="tm-icon-ref__col tm-icon-ref__col--glyph">Icon</span>
        </div>
        {ICON_MAP.map((row) => (
          <div className="tm-icon-ref__row" key={row.feature}>
            <span className="tm-icon-ref__col tm-icon-ref__col--feature">
              <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>{row.feature}</code>
            </span>
            <span className="tm-icon-ref__col tm-icon-ref__col--ant">
              <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>{row.antIcon}</code>
            </span>
            <span className="tm-icon-ref__col tm-icon-ref__col--glyph">
              <span className="tm-icon-ref__glyph">{row.icon}</span>
            </span>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Icon sizing: Box vs Glyph">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7, marginBottom: T.space.md }}>
        Two distinct sizing concepts: <strong style={{ color: C.text }}>Glyph</strong> (font-size of the icon itself) and <strong style={{ color: C.text }}>Box</strong> (click-target container that wraps the icon). Never mix them.
      </div>
      <div style={{ display: 'flex', gap: T.space.lg, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px' }}>
          <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: T.space.xs }}>Glyph sizes (font-size)</div>
          <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[
              { label: 'XS', token: '$icon-size-xs', px: T.icon.xs },
              { label: 'SM', token: '$icon-size-sm', px: T.icon.sm },
              { label: 'MD', token: '$icon-size-md', px: T.icon.md },
              { label: 'LG', token: '$icon-size-lg', px: T.icon.lg },
              { label: 'XL', token: '$icon-size-xl', px: T.icon.xl },
              { label: '2XL', token: '$icon-size-2xl', px: T.icon.xxl },
            ].map((ic) => (
              <div key={ic.label} style={{ textAlign: 'center', minWidth: 48 }}>
                <div style={{ fontSize: ic.px, color: C.text, marginBottom: T.space.xxs }}>
                  <SettingOutlined />
                </div>
                <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted }}>{ic.px}px</div>
                <div style={{ fontSize: T.type.caption, color: C.faint }}>{ic.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: '1 1 280px' }}>
          <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: T.space.xs }}>Box sizes (click target)</div>
          <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[
              { label: 'XS', token: '$icon-box-xs', px: T.iconBox.xs },
              { label: 'SM', token: '$icon-box-sm', px: T.iconBox.sm },
              { label: 'MD', token: '$icon-box-md', px: T.iconBox.md },
              { label: 'LG', token: '$icon-box-lg', px: T.iconBox.lg },
              { label: 'XL', token: '$icon-box-xl', px: T.iconBox.xl },
            ].map((ic) => (
              <div key={ic.label} style={{ textAlign: 'center', minWidth: 48 }}>
                <div style={{
                  width: ic.px, height: ic.px, borderRadius: T.radius.md,
                  background: primaryRgba(0.08), border: `1px solid ${primaryRgba(0.15)}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: T.icon.md, color: C.text, marginBottom: T.space.xxs,
                }}>
                  <SettingOutlined />
                </div>
                <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted }}>{ic.px}px</div>
                <div style={{ fontSize: T.type.caption, color: C.faint }}>{ic.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubSection>

    <SubSection label="Usage notes">
      <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap' }}>
        {[
          { label: 'Library', value: '@ant-design/icons' },
          { label: 'Variant', value: 'Outlined (default)' },
          { label: 'Import style', value: 'Named imports' },
          { label: 'Source', value: 'SidebarIcon switch' },
        ].map((item) => (
          <div key={item.label} style={{
            padding: `${T.space.xs}px ${T.space.md}px`, background: 'var(--preview-glass-subtle)',
            borderRadius: T.radius.md, border: '1px solid var(--preview-border)', flex: '1 1 160px',
          }}>
            <div style={{ fontSize: T.type.caption, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: T.weight.semibold, marginBottom: T.space.xxs }}>{item.label}</div>
            <code style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.body }}>{item.value}</code>
          </div>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// Theme Tweaker
// ---------------------------------------------------------------------------

const ThemeTweaker = ({ primaryColor, sidebarBg, onPrimaryChange, onSidebarChange, onReset, darkMode, onDarkToggle }) => (
  <div className="task-design-preview__tweaker">
    <span className="task-design-preview__tweaker-label">Theme Tweaker</span>
    <div className="task-design-preview__tweaker-group">
      <input
        type="color"
        className="task-design-preview__tweaker-input"
        value={primaryColor}
        onChange={(e) => onPrimaryChange(e.target.value)}
        title="Primary color"
      />
      <span className="task-design-preview__tweaker-name">Primary</span>
    </div>
    <div className="task-design-preview__tweaker-group">
      <input
        type="color"
        className="task-design-preview__tweaker-input"
        value={sidebarBg}
        onChange={(e) => onSidebarChange(e.target.value)}
        title="Sidebar background"
      />
      <span className="task-design-preview__tweaker-name">Sidebar BG</span>
    </div>
    <button
      className={`task-design-preview__tweaker-dark${darkMode ? ' task-design-preview__tweaker-dark--active' : ''}`}
      type="button"
      onClick={onDarkToggle}
      title="Toggle dark mode"
    >
      {darkMode ? '\u2600' : '\u263E'}
    </button>
    <button
      className="task-design-preview__tweaker-reset"
      type="button"
      onClick={onReset}
    >
      Reset
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Type Scale governance data (merged into sec-8 Typography)
// ---------------------------------------------------------------------------

const TYPE_SCALE_RULES = [
  { size: 12, token: '$type-caption',  role: 'Captions, badges, timestamps, compact UI',            aliases: '$font-size-2xs, $font-size-xs, $font-size-sm', weight: 400, lineHeight: 1.4 },
  { size: 13, token: '$type-small',    role: 'Secondary text, table cells, input fields',           aliases: '$font-size-md', weight: 400, lineHeight: 1.5 },
  { size: 14, token: '$type-button',   role: 'Button labels, interactive controls',                 aliases: null, weight: 500, lineHeight: 1.4 },
  { size: 15, token: '$type-body',     role: 'Primary body text (base)',                            aliases: '$font-size-base', weight: 400, lineHeight: 1.6 },
  { size: 16, token: '$type-body-lg',  role: 'Sub-headings, empty-state titles',                    aliases: '$font-size-lg', weight: 400, lineHeight: 1.6 },
  { size: 18, token: '$type-h3',       role: 'Drawer headings, card titles',                        aliases: '$font-size-xl', weight: 600, lineHeight: 1.3 },
  { size: 22, token: '$type-h2',       role: 'Page titles, section headings',                       aliases: '$font-size-2xl', weight: 600, lineHeight: 1.25 },
  { size: 28, token: '$type-h1',       role: 'Hero headings, large display text',                   aliases: '$font-size-3xl, $font-size-4xl', weight: 600, lineHeight: 1.2 },
];

// TypeScalePreview removed — content merged into TypographyPreview (sec-8)

// ---------------------------------------------------------------------------
// 3. Spacing & Grid (Foundations)
// ---------------------------------------------------------------------------

const SPACING_SCALE = [
  { px: 4,  token: '$spacing-xxs', role: 'Micro adjustments, icon gaps' },
  { px: 8,  token: '$spacing-xs',  role: 'Tight padding, compact gaps' },
  { px: 12, token: '$spacing-sm',  role: 'Compact spacing, input padding' },
  { px: 16, token: '$spacing-md',  role: 'Standard spacing, card padding' },
  { px: 24, token: '$spacing-lg',  role: 'Comfortable padding, section gaps' },
  { px: 32, token: '$spacing-xl',  role: 'Generous padding, major sections' },
  { px: 48, token: '$spacing-2xl', role: 'Page sections, hero padding' },
  { px: 64, token: '$spacing-3xl', role: 'Page-level divisions' },
];

const GRID_TOKENS = [
  { token: '$grid-max-width',          value: '1200px', desc: 'Content max-width' },
  { token: '$grid-sidebar-expanded',   value: '240px',  desc: 'Sidebar expanded' },
  { token: '$grid-sidebar-collapsed',  value: '56px',   desc: 'Sidebar collapsed' },
  { token: '$grid-kanban-column',      value: '280px',  desc: 'Kanban column width' },
  { token: '$grid-gutter',            value: '24px',   desc: 'Page gutter' },
  { token: '$grid-gutter-compact',    value: '16px',   desc: 'Compact gutter' },
];

const SpacingPreview = () => (
  <Section id="sec-3" title="3. Spacing & Grid" description="8-value spacing scale and layout grid tokens. Micro-corrections (1-3px) allowed for optical alignment on badges and labels.">
    <SubSection label="Spacing scale">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
        {SPACING_SCALE.map((s) => (
          <div key={s.px} style={{ textAlign: 'center', width: 80 }}>
            <div style={{
              width: s.px, height: s.px, maxWidth: 64, maxHeight: 64,
              background: primaryRgba(0.15), border: `1px solid ${primaryRgba(0.3)}`,
              borderRadius: T.radius.xs, margin: `0 auto ${T.space.xs}px`,
            }} />
            <div style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.text, fontWeight: T.weight.medium }}>{s.px}px</div>
            <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginTop: 2 }}>{s.token}</div>
            <div style={{ fontSize: T.type.caption, color: C.faint, marginTop: 2 }}>{s.role}</div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Layout grid">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {GRID_TOKENS.map((g) => (
          <div key={g.token} className="task-design-preview__token-row" style={{ display: 'flex', alignItems: 'center', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap', padding: `${T.space.xxs}px 0` }}>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.label, minWidth: 140, flexShrink: 0 }}>{g.token}</code>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.text, fontWeight: T.weight.medium, minWidth: 48 }}>{g.value}</code>
            <span style={{ fontSize: T.type.caption, color: C.secondary, flex: '1 1 100px' }}>{g.desc}</span>
          </div>
        ))}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// Motion governance data (merged into sec-14 Motion & Timing)
// ---------------------------------------------------------------------------

// MOTION_RULES removed — content consolidated into TIMING_TOKENS + GOVERNANCE_RULES

// ---------------------------------------------------------------------------
// 25. Governance & Rules (Governance)
// ---------------------------------------------------------------------------

const GOVERNANCE_RULES = [
  { area: 'Typography',     rule: '7 sizes: 12/13/15/16/18/22/28. Weights: 400/500/600 only.' },
  { area: 'Spacing',        rule: '8 values: 4/8/12/16/24/32/48/64. Micro-corrections (1-3px) allowed.' },
  { area: 'Motion',         rule: 'Single easing: $ease-contemporary. Timing: 80/120/180/280/400ms.' },
  { area: 'Letter-spacing', rule: '4 values: -0.02em, -0.01em, 0.01em, 0.04em.' },
  { area: 'Uppercase',      rule: 'Only on 12px captions with 0.04em tracking.' },
  { area: 'Shadows',        rule: 'Use $shadow-* tokens. Hardcoded shadows only for theme-aware gradients.' },
  { area: 'Fonts',          rule: 'Plus Jakarta Sans (400/500/600), JetBrains Mono (400/500).' },
  { area: 'Dates',          rule: '4 formats: full (MMM DD, YYYY h:mm A), short (MMM DD h:mm A), dateOnly (MMM DD, YYYY), compact (MM/DD).' },
];

const GovernancePreview = () => (
  <Section id="sec-25" title="25. Governance & Rules" description="Anti-drift rules, interaction state contracts, and color usage rules that keep the design system consistent.">
    <SubSection label="Rules at a glance">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {GOVERNANCE_RULES.map((g) => (
          <div key={g.area} className="task-design-preview__token-row" style={{ display: 'flex', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap', padding: `${T.space.xs}px 0`, borderBottom: '1px solid ' + C.border }}>
            <div style={{ minWidth: 100, fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, flexShrink: 0 }}>{g.area}</div>
            <div style={{ fontSize: T.type.small, color: C.body, flex: '1 1 200px' }}>{g.rule}</div>
          </div>
        ))}
      </div>
    </SubSection>

    {/* Interaction States (merged from Interaction State System) */}
    <InteractionContent />

    {/* Color Usage Rules (merged from Color Usage Rules) */}
    <ColorRulesContent />
  </Section>
);

// ---------------------------------------------------------------------------
// 26. Date Formats (Governance)
// ---------------------------------------------------------------------------

const DATE_FORMATS = [
  { label: 'Full',     token: 'dateFormat.full',     format: 'MMM DD, YYYY h:mm A', example: 'Jan 05, 2025 2:30 PM', desc: 'Timestamps, activity logs, test signals' },
  { label: 'Short',    token: 'dateFormat.short',    format: 'MMM DD h:mm A',       example: 'Jan 05 2:30 PM',       desc: 'Recent activity, compact timestamps' },
  { label: 'Date Only', token: 'dateFormat.dateOnly', format: 'MMM DD, YYYY',        example: 'Jan 05, 2025',         desc: 'Milestones, due dates, project dates' },
  { label: 'Compact',  token: 'dateFormat.compact',  format: 'MM/DD',               example: '01/05',                desc: 'Dense tables, sparkline labels, axis ticks' },
];

const DateFormatsPreview = () => (
  <Section id="sec-26" title="26. Date Formats" description="Governed format strings for consistent date/time display. Import from designTokens.js and pass to moment().format() or dayjs().format().">
    <SubSection label="Format tiers">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {DATE_FORMATS.map((d) => (
          <div key={d.token} className="task-design-preview__token-row" style={{ display: 'flex', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap', padding: `${T.space.sm}px 0`, borderBottom: '1px solid ' + C.border, alignItems: 'baseline' }}>
            <div style={{ minWidth: 80, fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, flexShrink: 0 }}>{d.label}</div>
            <div style={{ minWidth: 180, fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, flexShrink: 0 }}>{d.format}</div>
            <div style={{ minWidth: 180, fontFamily: C.sans, fontSize: T.type.body, fontWeight: T.weight.medium, color: C.text, flexShrink: 0 }}>{d.example}</div>
            <div style={{ fontSize: T.type.small, color: C.body, flex: '1 1 200px' }}>{d.desc}</div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Usage">
      <div style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.body, background: C.bgAlt, padding: T.space.md, borderRadius: T.radius.md, lineHeight: 1.7, whiteSpace: 'pre' }}>
{`import { dateFormat } from '../configs/designTokens';

moment(date).format(dateFormat.full)      // "Jan 05, 2025 2:30 PM"
moment(date).format(dateFormat.short)     // "Jan 05 2:30 PM"
moment(date).format(dateFormat.dateOnly)  // "Jan 05, 2025"
moment(date).format(dateFormat.compact)   // "01/05"`}
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// 24. Component Size Contract (Governance)
// ---------------------------------------------------------------------------

const SIZE_BUTTONS = [
  { label: 'Small',   token: '$btn-height-sm',      px: 32, padding: `${T.space.xs}px ${T.space.sm}px`,  fontSize: '$type-caption (12)', radius: '$radius-sm (6)',  desc: 'Table actions, compact UI' },
  { label: 'Medium',  token: '$btn-height-md',      px: 36, padding: `${T.space.xs}px ${T.space.md}px`,  fontSize: '$type-small (13)',   radius: '$radius-md (8)',  desc: 'Secondary actions, toolbars' },
  { label: 'Default', token: '$btn-height-default', px: 40, padding: `${T.space.xs}px ${T.space.md}px`,  fontSize: '$type-button (14)', radius: '$radius-input (10)', desc: 'Standard forms, primary CTA' },
  { label: 'Large',   token: '$btn-height-lg',      px: 44, padding: '10px 24px', fontSize: '$type-body (15)',   radius: '$radius-lg (12)', desc: 'Hero actions, touch targets' },
];

const SIZE_INPUTS = [
  { label: 'Compact', token: '$input-height-sm',     px: 32, desc: 'Compact inputs, inline edits' },
  { label: 'Default', token: '$input-height-md',     px: 40, desc: 'Standard inputs, selects' },
  { label: 'Large',   token: '$input-height-lg',     px: 44, desc: 'Touch-optimized, prominent inputs' },
];

const SIZE_ICONS = [
  { label: 'SM', token: '$icon-size-sm', px: 14, desc: 'Button icons, table actions' },
  { label: 'MD', token: '$icon-size-md', px: 16, desc: 'Standard icons in buttons' },
  { label: 'LG', token: '$icon-size-xl', px: 20, desc: 'Sidebar icons, headers' },
];

const SIZE_ICONS_FULL = [
  { label: 'XS', token: '$icon-size-xs', px: 12, desc: 'Small badges, inline indicators' },
  { label: 'SM', token: '$icon-size-sm', px: 14, desc: 'Button icons, table actions' },
  { label: 'MD', token: '$icon-size-md', px: 16, desc: 'Standard icons in buttons' },
  { label: 'LG', token: '$icon-size-lg', px: 18, desc: 'Prominent icons' },
  { label: 'XL', token: '$icon-size-xl', px: 20, desc: 'Sidebar icons, headers' },
  { label: '2XL', token: '$icon-size-2xl', px: 24, desc: 'Feature icons, empty states' },
];

const SIZE_AVATARS = [
  { label: 'XS',  token: '$avatar-xs', px: 18, desc: 'Mobile compact lists' },
  { label: 'SM',  token: '$avatar-sm', px: 20, desc: 'Compact lists, mentions' },
  { label: 'MD',  token: '$avatar-md', px: 24, desc: 'Comment threads, table cells' },
  { label: 'LG',  token: '$avatar-lg', px: 32, desc: 'Header profiles, sidebar avatars' },
];

const SIZE_BADGES = [
  { label: 'Small',   token: '$badge-sm',      px: 18, desc: 'Notification dots, status pips' },
  { label: 'Default', token: '$badge-default',  px: 22, desc: 'Count badges, nav indicators' },
];

const SIZE_CARDS = [
  { label: 'Compact',  token: '$card-padding-sm', px: 16, desc: 'Mobile cards, dense layouts' },
  { label: 'Standard', token: '$card-padding',    px: 20, desc: 'Default card padding' },
  { label: 'Large',    token: '$card-padding-lg', px: 24, desc: 'Feature cards, hero panels' },
  { label: 'Head Height', token: '$card-head-height', px: 56, desc: 'Card header min-height' },
  { label: 'Head Pad Y',  token: '$card-head-padding-y', px: 16, desc: 'Card header vertical padding (on .ant-card-head)' },
];

const SIZE_TABLE = [
  { label: 'Comfortable', token: '$table-row-comfortable', px: 44, desc: 'Default row height, meets WCAG touch target' },
  { label: 'Compact',     token: '$table-row-compact',     px: 36, desc: 'Dense data tables, search results' },
  { label: 'Header',      token: '$table-header-height',   px: 36, desc: 'Table header row' },
];

const SIZE_HEADERS = [
  { label: 'Page',    token: '$page-header-height',   px: 64, desc: 'Top-level page header bar' },
  { label: 'Sub',     token: '$sub-header-height',    px: 48, desc: 'Sub-navigation, filter bar' },
  { label: 'Mobile',  token: '$mobile-header-height', px: 56, desc: 'Mobile app header' },
  { label: 'Touch',   token: '$touch-min',            px: 44, desc: 'WCAG 2.5.5 minimum target' },
];

const SizeRow = ({ items, renderSpecimen }) => (
  <div style={{ display: 'flex', gap: T.space.lg, flexWrap: 'wrap', alignItems: 'flex-end' }}>
    {items.map((item) => (
      <div key={item.token} style={{ textAlign: 'center', minWidth: 72 }}>
        {renderSpecimen(item)}
        <div style={{ fontWeight: T.weight.medium, fontSize: T.type.caption, color: C.text, marginTop: T.space.xs }}>{item.label}</div>
        <div style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, marginTop: 2 }}>{item.token}</div>
        <div style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.faint, marginTop: 2 }}>{item.px}px</div>
      </div>
    ))}
  </div>
);

const ComponentSizingPreview = () => (
  <Section id="sec-24" title="24. Component Size Contract" description="Locked mechanical dimensions for every interactive element. No arbitrary px values — every height resolves to a governed token.">
    <SubSection label="Button Size Contract">
      <SizeRow items={SIZE_BUTTONS} renderSpecimen={(s) => (
        <div style={{
          height: s.px, minWidth: 96, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: primaryRgba(0.1), border: `1px solid ${primaryRgba(0.25)}`,
          borderRadius: T.radius.md, fontSize: T.type.small, fontWeight: T.weight.medium, color: C.text, padding: `0 ${T.space.md}px`,
        }}>
          Action
        </div>
      )} />
      <div style={{ marginTop: T.space.md, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.type.caption, fontFamily: C.mono }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}`, textAlign: 'left' }}>
              {['Size', 'Height', 'Padding', 'Font Size', 'Radius', 'Use Case'].map((h) => (
                <th key={h} style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, fontWeight: T.weight.semibold, color: C.label, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SIZE_BUTTONS.map((b) => (
              <tr key={b.label} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, fontWeight: T.weight.medium, color: C.text }}>{b.label}</td>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, color: C.muted }}>{b.px}px</td>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, color: C.muted }}>{b.padding}</td>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, color: C.muted }}>{b.fontSize}</td>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, color: C.muted }}>{b.radius}</td>
                <td style={{ padding: `${T.space.xxs}px ${T.space.xs}px`, color: C.faint, fontFamily: C.sans }}>{b.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SubSection>

    <SubSection label="Inputs">
      <SizeRow items={SIZE_INPUTS} renderSpecimen={(s) => (
        <div style={{
          height: s.px, width: 140, display: 'inline-flex', alignItems: 'center',
          background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: T.radius.md,
          fontSize: T.type.small, color: C.muted, padding: `0 ${T.space.sm}px`,
        }}>
          Placeholder...
        </div>
      )} />
    </SubSection>

    <SubSection label="Icons — Core 3">
      <SizeRow items={SIZE_ICONS} renderSpecimen={(s) => (
        <div style={{
          width: s.px, height: s.px, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: primaryRgba(0.08), borderRadius: T.radius.xs, fontSize: s.px * 0.7, color: C.label,
        }}>
          &#9670;
        </div>
      )} />
      <div style={{ fontSize: T.type.caption, color: C.secondary, marginTop: T.space.xs }}>
        Most components should use only SM / MD / LG. Full 6-step scale below for edge cases.
      </div>
    </SubSection>

    <SubSection label="Icons — Full scale">
      <SizeRow items={SIZE_ICONS_FULL} renderSpecimen={(s) => (
        <div style={{
          width: s.px, height: s.px, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: primaryRgba(0.05), borderRadius: T.radius.xs, fontSize: s.px * 0.7, color: C.muted,
        }}>
          &#9670;
        </div>
      )} />
    </SubSection>

    <SubSection label="Avatars">
      <SizeRow items={SIZE_AVATARS} renderSpecimen={(s) => (
        <div style={{
          width: s.px, height: s.px, borderRadius: '50%',
          background: `linear-gradient(135deg, ${primaryRgba(0.7)}, ${primaryRgba(0.4)})`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: s.px * 0.45, fontWeight: T.weight.semibold, color: T.color.onDark,
        }}>
          RG
        </div>
      )} />
    </SubSection>

    <SubSection label="Badges">
      <SizeRow items={SIZE_BADGES} renderSpecimen={(s) => (
        <div style={{
          height: s.px, minWidth: s.px, borderRadius: s.px, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', padding: `0 ${T.space.xs}px`,
          background: primaryRgba(0.12), fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text,
          fontFamily: C.mono,
        }}>
          {s.label === 'SM' ? '' : '5'}
        </div>
      )} />
    </SubSection>

    <SubSection label="Card padding">
      <SizeRow items={SIZE_CARDS} renderSpecimen={(s) => (
        <div style={{
          width: 96, height: 64, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: C.bgAlt, border: `1px solid ${C.border}`, borderRadius: T.radius.lg,
          padding: s.px, fontSize: T.type.caption, color: C.muted,
        }}>
          <div style={{ width: '100%', height: '100%', background: primaryRgba(0.06), borderRadius: T.radius.sm, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.type.caption }}>
            {s.px}px
          </div>
        </div>
      )} />
    </SubSection>

    <SubSection label="Table density">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {SIZE_TABLE.map((t) => (
          <div key={t.token} style={{
            display: 'flex', alignItems: 'center', height: t.px,
            background: C.bgAlt, borderRadius: T.radius.md, padding: `0 ${T.space.md}px`, gap: T.space.md,
            borderBottom: '1px solid ' + C.border,
          }}>
            <div style={{ fontSize: T.type.small, color: C.text, fontWeight: T.weight.medium, width: 96 }}>{t.label}</div>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, flex: 1 }}>{t.token}</code>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.label, width: 40, textAlign: 'right' }}>{t.px}px</code>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Headers & touch targets">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {SIZE_HEADERS.map((h) => (
          <div key={h.token} style={{
            display: 'flex', alignItems: 'center', height: h.px,
            background: h.token === '$touch-min' ? primaryRgba(0.06) : C.bgAlt,
            borderRadius: T.radius.md, padding: `0 ${T.space.md}px`, gap: T.space.md,
            border: h.token === '$touch-min' ? `1px dashed ${primaryRgba(0.3)}` : '1px solid ' + C.border,
          }}>
            <div style={{ fontSize: T.type.small, color: C.text, fontWeight: T.weight.medium, width: 96 }}>{h.label}</div>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, flex: 1 }}>{h.token}</code>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.label, width: 40, textAlign: 'right' }}>{h.px}px</code>
            <span style={{ fontSize: T.type.caption, color: C.secondary }}>{h.desc}</span>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Alignment rule">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Vertical alignment:</strong> Buttons (<code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$btn-height-md: 40px</code>) and inputs (<code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$input-height-md: 40px</code>) share the same height — they sit flush in any inline layout.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Icon containment:</strong> <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$icon-size-2xl: 24px</code> fits inside <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$btn-height-sm: 32px</code> with 4px vertical padding.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Touch targets:</strong> All mobile variants meet <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$touch-min: 44px</code> (WCAG 2.5.5).</div>
        <div><strong style={{ color: C.text }}>Avatar scale:</strong> 18 → 20 → 24 → 32px — compact sizes in 2-4px steps, large jump to 32px for prominent display.</div>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// Elevation Contract (content-only — merged into Shadows & Elevation)
// ---------------------------------------------------------------------------

const ELEVATION_LEVELS = [
  {
    level: 0,
    name: 'Flat',
    shadow: 'none',
    usage: 'Containers, backgrounds, input fields at rest',
    components: 'Column shells, page canvas, table rows',
  },
  {
    level: 1,
    name: 'Resting',
    shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    token: '$elevation-1 / $shadow-sm',
    usage: 'Cards at rest, subtle lift',
    components: 'Kanban cards, drawer inner cards',
  },
  {
    level: 2,
    name: 'Raised',
    shadow: '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)',
    token: '$elevation-2 / $shadow-md',
    usage: 'Card hover, button hover, lifted elements',
    components: 'Hovered cards, elevated panels',
  },
  {
    level: 3,
    name: 'Floating',
    shadow: '0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.03)',
    token: '$elevation-3 / $shadow-lg',
    usage: 'Dropdowns, popovers, menus',
    components: 'Select dropdowns, tooltip panels',
  },
  {
    level: 4,
    name: 'Modal',
    shadow: '0 20px 25px rgba(0,0,0,0.06), 0 8px 10px rgba(0,0,0,0.04)',
    token: '$elevation-4 / $shadow-xl',
    usage: 'Modals, full-page overlays',
    components: 'Delete confirmation, task creation modal',
  },
  {
    level: 'Overlay',
    name: 'Drawer',
    shadow: '-8px 0 32px rgba(0,0,0,0.08), -24px 0 64px rgba(0,0,0,0.05)',
    token: '$elevation-overlay / $shadow-drawer',
    usage: 'Drawer panels, side sheets',
    components: 'Task detail drawer',
  },
];

const ElevationContent = () => (
  <>
    <SubSection label="Elevation contract (6 levels)">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: T.space.xl, alignItems: 'flex-start' }}>
        {ELEVATION_LEVELS.map((e) => (
          <div key={e.level} style={{ textAlign: 'center', width: 120 }}>
            <div style={{
              width: 100, height: 100, borderRadius: T.radius.lg,
              background: 'var(--preview-card-bg)',
              boxShadow: e.shadow,
              margin: `0 auto ${T.space.sm}px`,
              border: e.shadow === 'none' ? '1px solid var(--preview-border)' : '1px solid var(--preview-subtle-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: T.type.h2, fontWeight: T.weight.semibold, color: C.muted,
            }}>
              {e.level}
            </div>
            <div style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text }}>{e.name}</div>
            {e.token && (
              <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginTop: T.space.xxs }}>{e.token}</div>
            )}
            <div style={{ fontSize: T.type.caption, color: C.secondary, marginTop: T.space.xxs }}>{e.usage}</div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Elevation component mapping">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {ELEVATION_LEVELS.map((e) => (
          <div key={e.level} className="task-design-preview__token-row" style={{
            display: 'flex', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap', padding: `${T.space.xs}px 0`,
            borderBottom: '1px solid ' + C.border,
          }}>
            <div style={{ minWidth: 80, fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text, flexShrink: 0 }}>
              Level {e.level}
            </div>
            <div style={{ fontSize: T.type.small, color: C.body, flex: '1 1 200px' }}>{e.components}</div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Shadow governance">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>No custom shadows.</strong> Every <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>box-shadow</code> must resolve to one of the 6 elevation tokens.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Hover = +1 level.</strong> Cards at elevation-1 go to elevation-2 on hover. Buttons at elevation-0 go to elevation-1.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Accent shadows</strong> (<code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$shadow-primary-*</code>) are allowed as additive glows on focused/selected elements, not as replacements.</div>
        <div><strong style={{ color: C.text }}>Focus ring</strong> (<code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$shadow-focus</code>) is a separate system — not an elevation level.</div>
      </div>
    </SubSection>
  </>
);

// ---------------------------------------------------------------------------
// Interaction States (content-only — merged into Governance)
// ---------------------------------------------------------------------------

const INTERACTION_STATES = [
  {
    state: 'Hover',
    description: 'Mouse enters interactive element',
    rules: [
      'Cards: translateY(-2px) + shadow elevation-1 → elevation-2',
      'Buttons: translateY(-1px) + shadow increase',
      'Links/text: color change only (no transform)',
      'Duration: 180ms $ease-contemporary',
    ],
  },
  {
    state: 'Active / Press',
    description: 'Mouse button down or touch press',
    rules: [
      'scale(0.98) for all interactive elements',
      'Duration: 80ms ease-out ($transition-micro)',
      'Shadow returns to resting level',
      'No color change on press — only on release',
    ],
  },
  {
    state: 'Focus',
    description: 'Element receives keyboard focus',
    rules: [
      'Outline: 2px solid $focus-ring-color (#2563eb)',
      'Outline-offset: 2px',
      'Or glow: 0 0 0 3px rgba(primary, 0.15)',
      'Never remove :focus-visible — a11y requirement',
    ],
  },
  {
    state: 'Disabled',
    description: 'Element is non-interactive',
    rules: [
      'opacity: 0.5',
      'cursor: not-allowed',
      'No hover, active, or focus effects',
      'pointer-events: none (optional, for click-through)',
    ],
  },
  {
    state: 'Selected',
    description: 'Element is chosen/active among peers',
    rules: [
      'border: 2px solid $task-primary',
      'Optional glow ring: 0 0 0 3px rgba(primary, 0.15)',
      'Background may tint with $accent-primary-subtle',
      'Must remain distinguishable from focus state',
    ],
  },
];

const InteractionContent = () => (
  <>
    <SubSection label="Interaction states">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.lg }}>
        {INTERACTION_STATES.map((s) => (
          <div key={s.state} style={{
            display: 'flex', gap: T.space.lg, flexWrap: 'wrap', alignItems: 'flex-start',
          }}>
            {/* Specimen */}
            <div style={{ flex: '0 0 160px' }}>
              <div
                className={`task-design-preview__interaction-specimen task-design-preview__interaction-specimen--${s.state.toLowerCase().replace(/[\s/]+/g, '-')}`}
                style={{
                  width: 140, height: 80, borderRadius: T.radius.lg,
                  background: 'var(--preview-card-bg)',
                  border: s.state === 'Selected'
                    ? `2px solid var(--ant-primary-color, #1890ff)`
                    : '1px solid var(--preview-border)',
                  boxShadow: s.state === 'Focus'
                    ? `0 0 0 3px ${primaryRgba(0.15)}`
                    : s.state === 'Selected'
                      ? `0 0 0 3px ${primaryRgba(0.15)}, ${SHADOWS[1]?.value || ''}`
                      : s.state === 'Disabled'
                        ? 'none'
                        : SHADOWS[1]?.value || '',
                  opacity: s.state === 'Disabled' ? 0.5 : 1,
                  cursor: s.state === 'Disabled' ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: T.type.caption, fontWeight: T.weight.semibold, color: s.state === 'Disabled' ? C.muted : C.body,
                  outline: s.state === 'Focus' ? `2px solid ${T.color.focus}` : 'none',
                  outlineOffset: s.state === 'Focus' ? '2px' : undefined,
                  transform: s.state === 'Active / Press' ? 'scale(0.98)' :
                    s.state === 'Hover' ? 'translateY(-2px)' : undefined,
                  transition: 'all 180ms ease',
                }}
              >
                {s.state}
              </div>
            </div>
            {/* Rules */}
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xxs }}>{s.state}</div>
              <div style={{ fontSize: T.type.caption, color: C.secondary, marginBottom: T.space.xs }}>{s.description}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
                {s.rules.map((rule, i) => (
                  <div key={i} style={{
                    padding: `${T.space.xs}px ${T.space.sm}px`, background: 'var(--preview-glass-subtle)',
                    borderRadius: T.radius.md, fontSize: T.type.caption, color: C.body,
                    border: '1px solid ' + C.border,
                  }}>
                    <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>{rule}</code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Interaction governance">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Consistency:</strong> All interactive elements must follow the same state patterns. No per-component custom hover inventions.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>Timing:</strong> Hover/focus = <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>180ms $ease-contemporary</code>. Active = <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>80ms ease-out</code>.</div>
        <div><strong style={{ color: C.text }}>Transform stacking:</strong> Hover translateY and active scale may combine: <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>translateY(0) scale(0.98)</code> on press from hover.</div>
      </div>
    </SubSection>
  </>
);

// ---------------------------------------------------------------------------
// Color Usage Rules (content-only — merged into Governance)
// ---------------------------------------------------------------------------

const COLOR_ROLES = [
  {
    role: 'Primary',
    token: '$task-primary / $accent-primary',
    hex: '#1890ff',
    allowed: ['Main CTA buttons', 'Selected/active states', 'Focus rings', 'Active nav indicators', 'Links'],
    forbidden: [],
  },
  {
    role: 'Accent',
    token: '$accent-cyan',
    hex: '#26a69a',
    allowed: ['Secondary highlights', 'Info badges', 'Subtle emphasis'],
    forbidden: ['Primary CTAs', 'Form borders', 'Text body'],
  },
  {
    role: 'Surface',
    token: '$bg-card / $bg-container',
    hex: '#ffffff / #f4f5f7',
    allowed: ['Card backgrounds', 'Page canvas', 'Column shells', 'Input backgrounds'],
    forbidden: ['Badge fills', 'Button fills (use gradient)', 'Accent indicators'],
  },
  {
    role: 'Glass',
    token: '$bg-glass',
    hex: 'rgba(255,255,255,0.88)',
    allowed: ['Drawers', 'Modals', 'Floating panels'],
    forbidden: ['Cards', 'Inputs', 'Buttons', 'Table rows', 'Badges'],
  },
  {
    role: 'Gradient',
    token: 'linear-gradient(180deg, ...)',
    hex: 'lighten($primary,5%) → $primary',
    allowed: ['Primary buttons', 'Page background mesh', 'Hero sections'],
    forbidden: ['Inputs', 'Secondary buttons', 'Ghost buttons', 'Cards', 'Badges'],
  },
];

const ColorRulesContent = () => (
  <>
    <SubSection label="Color usage rules">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.md }}>
        {COLOR_ROLES.map((cr) => (
          <div key={cr.role} style={{ display: 'flex', gap: T.space.lg, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Swatch */}
            <div style={{ flex: '0 0 80px', textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: T.radius.lg, margin: `0 auto ${T.space.xs}px`,
                background: cr.hex.startsWith('rgba') || cr.hex.includes('/') ?
                  `linear-gradient(135deg, ${primaryRgba(0.15)}, ${primaryRgba(0.05)})` :
                  cr.hex.includes('→') ?
                    `linear-gradient(180deg, ${primaryRgba(0.8)}, var(--ant-primary-color, #1890ff))` :
                    cr.hex,
                border: '1px solid var(--preview-border)',
                boxShadow: '0 1px 3px var(--preview-border)',
              }} />
              <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.body }}>{cr.role}</div>
            </div>
            {/* Rules */}
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ fontSize: T.type.caption, fontFamily: C.mono, color: C.muted, marginBottom: T.space.xs }}>{cr.token}</div>
              <div style={{ display: 'flex', gap: T.space.sm, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: T.color.allowed, marginBottom: T.space.xs, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Allowed</div>
                  {cr.allowed.map((a, i) => (
                    <div key={i} style={{ fontSize: T.type.caption, color: C.body, padding: '3px 0' }}>
                      &#10003; {a}
                    </div>
                  ))}
                </div>
                {cr.forbidden.length > 0 && (
                  <div style={{ flex: '1 1 200px' }}>
                    <div style={{ fontSize: T.type.caption, fontWeight: T.weight.semibold, color: T.color.forbidden, marginBottom: T.space.xs, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Forbidden</div>
                    {cr.forbidden.map((f, i) => (
                      <div key={i} style={{ fontSize: T.type.caption, color: C.body, padding: '3px 0' }}>
                        &#10007; {f}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SubSection>
    <SubSection label="Token naming clarifications">
      <div style={{ fontSize: T.type.small, color: C.body, lineHeight: 1.7 }}>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>$accent-violet</strong> — Removed. Was a misleading alias for <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$accent-primary</code> (blue #1890ff, never violet). All shadow tokens renamed <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$shadow-primary-*</code>.</div>
        <div style={{ marginBottom: T.space.xs }}><strong style={{ color: C.text }}>$gradient-accent</strong> — Deprecated. Resolves to solid <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$accent-primary</code>. Use <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>$accent-primary</code> directly.</div>
        <div><strong style={{ color: C.text }}>Urgency colors</strong> — Fixed palette. Never override: critical=#dc2626, high=#f97316, medium=#eab308, low=#22c55e.</div>
      </div>
    </SubSection>
  </>
);

// ---------------------------------------------------------------------------
// Responsive System Contract
// ---------------------------------------------------------------------------

const BREAKPOINTS = [
  { name: 'Desktop', range: '\u2265 1200px', token: '$bp-desktop: 1200px', color: primaryRgba(0.8) },
  { name: 'Laptop',  range: '992 \u2013 1199px', token: '$bp-laptop: 992px',   color: primaryRgba(0.6) },
  { name: 'Tablet',  range: '768 \u2013 991px',  token: '$bp-tablet: 768px',   color: primaryRgba(0.4) },
  { name: 'Mobile',  range: '\u2264 767px',  token: '$bp-mobile: 767px',  color: primaryRgba(0.25) },
];

const TRANSFORMATION_RULES = [
  {
    breakpoint: 'Desktop (\u2265 1200px)',
    rules: [
      'Tables remain tables',
      'Multi-column layouts allowed',
      'Sidebar visible (expanded)',
      'All filter controls inline',
      'Full pagination bar',
    ],
  },
  {
    breakpoint: 'Tablet (768 \u2013 991px)',
    rules: [
      'Tables transform to card layout',
      'Sidebar collapses to icon rail',
      'Filters become slide-out drawer',
      'Two-column grid reduces to single where needed',
      'Kanban columns scroll horizontally',
    ],
  },
  {
    breakpoint: 'Mobile (\u2264 767px)',
    rules: [
      'Single column only \u2014 no horizontal scroll',
      'Cards only (no table view)',
      'Primary action always visible (FAB or sticky footer)',
      'Touch targets minimum 44px ($touch-min)',
      'Bottom sheet for secondary actions',
    ],
  },
];

const DENSITY_ADJUSTMENTS = [
  { element: 'Button height',   desktop: '40px ($btn-height-md)',   tablet: '40px',  mobile: '48px ($btn-height-mobile)' },
  { element: 'Card padding',    desktop: '20px ($space-5)',         tablet: '16px ($space-4)', mobile: '12px ($space-3)' },
  { element: 'Table row height', desktop: '44px ($table-row-comfortable)', tablet: 'N/A (cards)', mobile: 'N/A (cards)' },
  { element: 'Base font size',  desktop: '15px ($font-size-base)',  tablet: '15px',  mobile: '15px (unchanged)' },
  { element: 'Input height',    desktop: '40px ($input-height-md)', tablet: '40px',  mobile: '44px ($input-height-lg)' },
  { element: 'Avatar size',     desktop: '24px ($avatar-md)',       tablet: '24px',  mobile: '20px ($avatar-sm)' },
];

const TABLE_TO_CARD_RULES = [
  { rule: 'Primary fields always visible', desc: 'Task name, status badge, and assignee must render in every card without interaction.' },
  { rule: 'Secondary fields collapsible', desc: 'Due date, priority, tags, and description collapse behind a "Show more" toggle.' },
  { rule: 'Max 3 metadata items per row', desc: 'Card metadata strip shows at most 3 items. Additional items overflow into the collapsible area.' },
  { rule: 'Priority border preserved', desc: 'Left-edge priority color strip remains on cards, matching the table row border pattern.' },
  { rule: 'Action menu via long-press or kebab', desc: 'Row actions (edit, delete, move) surface through a kebab icon or long-press menu on mobile.' },
];

const ResponsiveContractPreview = () => (
  <Section
    id="sec-21"
    title="21. Breakpoints"
    description="Official breakpoints, transformation rules, and density adjustments. No feature may invent its own responsive behavior outside this contract."
  >
    <SubSection label="Breakpoints">
      <div style={{ display: 'flex', gap: T.space.md, flexWrap: 'wrap' }}>
        {BREAKPOINTS.map((bp) => (
          <div key={bp.name} style={{
            flex: '1 1 160px', padding: `${T.space.md}px ${T.space.lg}px`, borderRadius: T.radius.lg,
            background: 'var(--preview-subtle-bg)', border: '1px solid var(--preview-border)',
            textAlign: 'center',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: T.radius.lg, margin: `0 auto ${T.space.sm}px`,
              background: bp.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: T.type.h3, color: T.color.onDark, fontWeight: T.weight.semibold }}>{bp.name[0]}</span>
            </div>
            <div style={{ fontWeight: T.weight.semibold, fontSize: T.type.small, color: C.text }}>{bp.name}</div>
            <div style={{ fontSize: T.type.small, color: C.body, marginTop: 2 }}>{bp.range}</div>
            <code style={{ fontFamily: C.mono, fontSize: T.type.caption, color: C.muted, marginTop: T.space.xs, display: 'block' }}>{bp.token}</code>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Transformation rules">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.md }}>
        {TRANSFORMATION_RULES.map((tr) => (
          <div key={tr.breakpoint} style={{
            padding: `${T.space.md}px ${T.space.md}px`, borderRadius: T.radius.input,
            background: 'var(--preview-subtle-bg)', border: '1px solid var(--preview-border)',
          }}>
            <div style={{ fontWeight: T.weight.semibold, fontSize: T.type.small, color: C.text, marginBottom: T.space.xs }}>{tr.breakpoint}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {tr.rules.map((r, i) => (
                <div key={i} style={{ fontSize: T.type.caption, color: C.body, paddingLeft: 14, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: C.muted }}>&bull;</span>
                  {r}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Density adjustments per breakpoint">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.type.caption }}>
          <thead>
            <tr>
              {['Element', 'Desktop', 'Tablet', 'Mobile'].map((h) => (
                <th key={h} style={{
                  textAlign: 'left', padding: `${T.space.xs}px ${T.space.sm}px`, fontWeight: T.weight.semibold, fontSize: T.type.caption,
                  color: C.label, borderBottom: '2px solid var(--preview-border)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DENSITY_ADJUSTMENTS.map((d) => (
              <tr key={d.element}>
                <td style={{ padding: `${T.space.xs}px ${T.space.sm}px`, fontWeight: T.weight.medium, color: C.text, borderBottom: '1px solid var(--preview-border)' }}>{d.element}</td>
                <td style={{ padding: `${T.space.xs}px ${T.space.sm}px`, fontFamily: C.mono, color: C.body, borderBottom: '1px solid var(--preview-border)' }}>{d.desktop}</td>
                <td style={{ padding: `${T.space.xs}px ${T.space.sm}px`, fontFamily: C.mono, color: C.body, borderBottom: '1px solid var(--preview-border)' }}>{d.tablet}</td>
                <td style={{ padding: `${T.space.xs}px ${T.space.sm}px`, fontFamily: C.mono, color: C.body, borderBottom: '1px solid var(--preview-border)' }}>{d.mobile}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SubSection>

    <SubSection label="Table-to-card transformation">
      <div style={{ display: 'flex', flexDirection: 'column', gap: T.space.xxs }}>
        {TABLE_TO_CARD_RULES.map((r) => (
          <div key={r.rule} style={{
            display: 'flex', gap: `${T.space.xxs}px ${T.space.md}px`, flexWrap: 'wrap',
            padding: `${T.space.xs}px 0`, borderBottom: '1px solid ' + C.border,
          }}>
            <div style={{ minWidth: 220, fontSize: T.type.caption, fontWeight: T.weight.semibold, color: C.text, flexShrink: 0 }}>{r.rule}</div>
            <div style={{ fontSize: T.type.caption, color: C.body, flex: '1 1 200px' }}>{r.desc}</div>
          </div>
        ))}
      </div>
    </SubSection>

    <SubSection label="Enforcement rule">
      <div style={{
        padding: `${T.space.md}px ${T.space.md}px`, borderRadius: T.radius.input,
        background: primaryRgba(0.06), border: `1px solid ${primaryRgba(0.2)}`,
      }}>
        <div style={{ fontSize: T.type.small, fontWeight: T.weight.semibold, color: C.text, marginBottom: T.space.xs }}>
          No feature may invent its own responsive behavior outside this contract.
        </div>
        <div style={{ fontSize: T.type.caption, color: C.body, lineHeight: 1.7 }}>
          All breakpoint-dependent layout changes must use the official breakpoint tokens and follow the transformation rules above.
          Custom <code style={{ fontFamily: C.mono, fontSize: T.type.caption }}>@media</code> queries with arbitrary widths are forbidden.
          If a component needs a responsive rule not covered here, extend this contract first.
        </div>
      </div>
    </SubSection>
  </Section>
);

// ---------------------------------------------------------------------------
// TOC data — one entry per section
// ---------------------------------------------------------------------------

const TOC_ITEMS = [
  // A — Foundations
  { id: 'sec-1',  label: 'Colors' },
  { id: 'sec-2',  label: 'Typography' },
  { id: 'sec-3',  label: 'Spacing' },
  { id: 'sec-4',  label: 'Radius' },
  { id: 'sec-5',  label: 'Elevation' },
  { id: 'sec-6',  label: 'Motion' },
  // B — Components
  { id: 'sec-7',  label: 'Buttons' },
  { id: 'sec-8',  label: 'Forms' },
  { id: 'sec-9',  label: 'Cards' },
  { id: 'sec-10', label: 'Badges' },
  { id: 'sec-11', label: 'Tables' },
  { id: 'sec-12', label: 'Modals' },
  { id: 'sec-13', label: 'Pagination' },
  { id: 'sec-14', label: 'Icons' },
  // C — Patterns
  { id: 'sec-15', label: 'Header' },
  { id: 'sec-16', label: 'Filters' },
  { id: 'sec-17', label: 'Kanban' },
  { id: 'sec-18', label: 'Drawer' },
  { id: 'sec-19', label: 'Comments' },
  { id: 'sec-20', label: 'Sidebar' },
  { id: 'sec-21', label: 'Breakpoints' },
  { id: 'sec-22', label: 'Responsive' },
  // D — Governance
  { id: 'sec-23', label: 'A11y' },
  { id: 'sec-24', label: 'Sizing' },
  { id: 'sec-25', label: 'Governance' },
  { id: 'sec-26', label: 'Dates' },
];

// Floating TOC navigation
const TableOfContents = () => {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    TOC_ITEMS.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <nav className="task-design-preview__toc" aria-label="Section navigation">
      {TOC_ITEMS.map((item) => (
        <div
          key={item.id}
          className={`task-design-preview__toc-dot${activeId === item.id ? ' task-design-preview__toc-dot--active' : ''}`}
          data-label={item.label}
          onClick={() => {
            const el = document.getElementById(item.id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          role="button"
          tabIndex={0}
          aria-label={`Jump to ${item.label}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const el = document.getElementById(item.id);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        />
      ))}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const TaskDesignPreview = () => {
  // Read live values from :root on mount, fall back to defaults
  const [primaryColor, setPrimaryColor] = useState(() => {
    const live = getCssVar('--ant-primary-color');
    return live && live.startsWith('#') ? live : DEFAULT_PRIMARY;
  });
  const [sidebarBg, setSidebarBg] = useState(() => {
    const live = getCssVar('--mj-sidebar-color');
    return live && live.startsWith('#') ? live : DEFAULT_SIDEBAR_BG;
  });
  const [darkMode, setDarkMode] = useState(false);
  const previewRef = useRef(null);
  const darkMountRef = useRef(true); // skip first render

  // Suppress transitions during dark/light switch so all sections change instantly
  useLayoutEffect(() => {
    if (darkMountRef.current) { darkMountRef.current = false; return; }
    const el = previewRef.current;
    if (!el) return;
    el.classList.add('task-design-preview--no-transition');
    // Force reflow so the browser applies no-transition before painting
    void el.offsetHeight; // eslint-disable-line no-unused-expressions
    const id = requestAnimationFrame(() => {
      el.classList.remove('task-design-preview--no-transition');
    });
    return () => cancelAnimationFrame(id);
  }, [darkMode]);

  const { isTablet } = useBreakpoints();

  // Store original colors on mount for cleanup
  const origColors = useRef({
    primary: getCssVar('--ant-primary-color') || DEFAULT_PRIMARY,
    sidebar: getCssVar('--mj-sidebar-color') || DEFAULT_SIDEBAR_BG,
  });

  // Sync color changes to :root via ColorChanger
  const handlePrimaryChange = useCallback((hex) => {
    setPrimaryColor(hex);
    updatePrimaryColor(hex);
  }, []);

  const handleSidebarChange = useCallback((hex) => {
    setSidebarBg(hex);
    updateSidebarColor(hex);
  }, []);

  const handleReset = useCallback(() => {
    setPrimaryColor(DEFAULT_PRIMARY);
    setSidebarBg(DEFAULT_SIDEBAR_BG);
    setDarkMode(false);
    updatePrimaryColor(DEFAULT_PRIMARY);
    updateSidebarColor(DEFAULT_SIDEBAR_BG);
  }, []);

  // Restore original colors when navigating away
  useEffect(() => {
    const orig = origColors.current;
    return () => {
      if (orig.primary) updatePrimaryColor(orig.primary);
      if (orig.sidebar) updateSidebarColor(orig.sidebar);
    };
  }, []);

  const isCustom = primaryColor !== DEFAULT_PRIMARY || sidebarBg !== DEFAULT_SIDEBAR_BG;

  return (
    <div ref={previewRef} className={`task-design-preview${darkMode ? ' task-design-preview--dark' : ''}`}>
      {/* Hero header */}
      <div className="task-design-preview__header">
        <div className="task-design-preview__meta-badge">
          <span className="task-design-preview__meta-dot" />
          Design System v3.1 &middot; 26 Sections &middot; 4 Groups
        </div>
        <h1 className="task-design-preview__title">Task Design System</h1>
        <p className="task-design-preview__subtitle">
          The complete reference for every component, token, and pattern in the task management facelift.
          {isCustom && ' Custom theme active.'}
        </p>
        <div className="task-design-preview__stats">
          <div className="task-design-preview__stat">
            <div className="task-design-preview__stat-value">140+</div>
            <div className="task-design-preview__stat-label">Tokens</div>
          </div>
          <div className="task-design-preview__stat">
            <div className="task-design-preview__stat-value">26</div>
            <div className="task-design-preview__stat-label">Sections</div>
          </div>
          <div className="task-design-preview__stat">
            <div className="task-design-preview__stat-value">4</div>
            <div className="task-design-preview__stat-label">Groups</div>
          </div>
          <div className="task-design-preview__stat">
            <div className="task-design-preview__stat-value">5</div>
            <div className="task-design-preview__stat-label">Durations</div>
          </div>
        </div>
      </div>

      {/* Floating TOC */}
      <TableOfContents />

      {/* Content area */}
      <div className="task-design-preview__content">
        <div className="task-design-preview__content-body">
          <ThemeTweaker
            primaryColor={primaryColor}
            sidebarBg={sidebarBg}
            onPrimaryChange={handlePrimaryChange}
            onSidebarChange={handleSidebarChange}
            onReset={handleReset}
            darkMode={darkMode}
            onDarkToggle={() => setDarkMode((prev) => !prev)}
          />

          {/* A — Foundations (1-6) */}
          <TokenSwatchPreview darkMode={darkMode} />
          <TypographyPreview />
          <SpacingPreview />
          <BorderRadiusPreview />
          <ShadowsPreview darkMode={darkMode} />
          <TransitionsPreview />

          {/* B — Components (7-14) */}
          <ButtonPreview />
          <FormsPreview />
          <CardVariantsPreview />
          <BadgeSystemPreview />
          <TablePreview isTablet={isTablet} />
          <ModalPreview />
          <PaginationPreview />
          <IconReferencePreview />

          {/* C — Patterns (15-22) */}
          <HeaderPreview />
          <FilterPreview />
          <KanbanPreview />
          <DrawerPreview />
          <CommentsPreview />
          <SidebarPreview />
          <ResponsiveContractPreview />
          <ResponsivePreview />

          {/* D — Governance (23-26) */}
          <AccessibilityPreview />
          <ComponentSizingPreview />
          <GovernancePreview />
          <DateFormatsPreview />
        </div>
      </div>
    </div>
  );
};

export default TaskDesignPreview;
